// Dathent API Client
// Centralized API client for all backend communication

import i18n from '../../i18n';
import { getUxAttribution } from '../../utils/uxTracker.js';
import { trackOnce } from '../../lib/funnel.js';

export class DathentAPIError extends Error {
  constructor(message, code, status, details) {
    super(message);
    this.name = 'DathentAPIError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

const RATE_LIMIT_RETRY_MAX_DELAY_MS = 4000;

/* ============================================================
   Multi-brand "active brand" localStorage helpers (2026-09-08 fix).
   Extracted as pure functions, storage-injectable, so the constructor's
   synchronous seed and BrandContext's persist/clear paths share ONE key
   format and can't drift — and so they're unit-testable without a DOM.

   Incident: the OLD key (`dathent.multibrand.active`, no owner) was
   global and unscoped. A previous account's (or a previous org's) brand
   id could ride along into a brand-new org's very first OAuth connect,
   because nothing recorded WHOSE value it was. The fix namespaces the
   value per user id and records who it belongs to
   (`dathent.multibrand.active.user`) so a mismatch can be detected and
   the value dropped instead of trusted.

   `readSeedActiveBrandId` is a best-effort SYNCHRONOUS read for the
   constructor below — real auth is a cookie + an async /me call, so
   there is no live "current session user" to compare against yet. It
   seeds from whichever user this browser tab last confirmed (via
   persistActiveBrand) owns the cached value — the common "same user
   reloads the page" case. BrandContext.jsx's load() reconciles against
   the live session right after and calls clearActiveBrand the instant
   the resolved user doesn't match, so a genuine account switch on the
   same browser is corrected as soon as /me resolves rather than trusted
   for the rest of the session.

   m6 (fix-round, 2026-09-08): a browser that already confirmed an owner
   via persistActiveBrand (the owner marker exists) may still be carrying
   the PRE-fix global key from before this namespacing shipped, if that
   tab hasn't re-persisted since. `readSeedActiveBrandId` migrates it
   ONCE into that owner's namespaced key — but ONLY when the owner marker
   is already present. A browser with NO marker at all still never reads
   the legacy key (m5's limitation stands: an ownerless value can't be
   attributed to anyone, so it's left for clearActiveBrand to wipe rather
   than trusted). Even in the worst case — the migrated value actually
   belonged to a DIFFERENT org than the live session resolves to — this is
   now harmless: auth-worker's resolveBrandProfileId (task A of this same
   fix) rejects server-side any brandProfileId that doesn't belong to the
   connecting org, falling back to that org's own active brand instead, so
   a stale id can no longer attach a social account to the wrong brand.
   ============================================================ */
const MULTIBRAND_ACTIVE_LEGACY_KEY = 'dathent.multibrand.active';
const MULTIBRAND_OWNER_KEY = 'dathent.multibrand.active.user';

export function multibrandActiveKey(userId) {
  return `${MULTIBRAND_ACTIVE_LEGACY_KEY}:${userId}`;
}

export function readActiveBrandOwner(storage) {
  try {
    return storage?.getItem(MULTIBRAND_OWNER_KEY) || null;
  } catch (_) {
    return null;
  }
}

export function readSeedActiveBrandId(storage) {
  const ownerUserId = readActiveBrandOwner(storage);
  if (!ownerUserId) return null;
  try {
    const key = multibrandActiveKey(ownerUserId);
    let value = storage.getItem(key) || null;
    if (!value) {
      // One-time migration (m6) — see this file's header comment above for
      // why this is safe to do even though we can't re-verify the legacy
      // value's true owner: it only runs when an owner marker already
      // exists, and a wrong guess is now caught server-side regardless.
      const legacy = storage.getItem(MULTIBRAND_ACTIVE_LEGACY_KEY);
      if (legacy) {
        storage.setItem(key, legacy);
        storage.removeItem(MULTIBRAND_ACTIVE_LEGACY_KEY);
        value = legacy;
      }
    }
    return value;
  } catch (_) {
    return null;
  }
}

export function persistActiveBrand(storage, userId, brandId) {
  try {
    if (!userId || !brandId) {
      clearActiveBrand(storage);
      return;
    }
    storage.setItem(MULTIBRAND_OWNER_KEY, String(userId));
    storage.setItem(multibrandActiveKey(userId), brandId);
  } catch (_) { /* non-fatal */ }
}

export function clearActiveBrand(storage) {
  try {
    const ownerUserId = storage?.getItem(MULTIBRAND_OWNER_KEY);
    if (ownerUserId) storage.removeItem(multibrandActiveKey(ownerUserId));
    storage.removeItem(MULTIBRAND_OWNER_KEY);
    // Opportunistically wipe the pre-fix global key too, so it can never
    // resurface and confuse an older cached tab.
    storage.removeItem(MULTIBRAND_ACTIVE_LEGACY_KEY);
  } catch (_) { /* non-fatal */ }
}

// Every environment mirrors the same shape: app.<env> is served by api.<env>
// (app.dathent.ai/api.dathent.ai, app.staging.dathent.ai/api.staging.dathent.ai,
// app.dev.dathent.ai/api.dev.dathent.ai, app.123-ci.dev.../api.123-ci.dev...).
// So the API host is DERIVED from wherever the app is actually running.
//
// The previous version returned production for any *.dathent.ai host, which
// meant a build that forgot VITE_API_URL pointed staging and dev straight at
// the production API — the same leak GH-164 found in the landing's signup and
// login links, but on the app itself, where it would create and mutate real
// customer data from a test environment. Deriving removes the hardcoded
// production fallback entirely: a non-production host can no longer resolve to
// production, whatever the build vars say.
function apiHostForAppHost(hostname) {
  const host = hostname.startsWith('www.') ? hostname.slice(4) : hostname;
  if (host.startsWith('app.') && host.endsWith('.dathent.ai')) {
    return `https://api.${host.slice('app.'.length)}`;
  }
  return null;
}

export function defaultBaseURL() {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  if (typeof window !== 'undefined') {
    const derived = apiHostForAppHost(window.location.hostname);
    if (derived) {
      return derived;
    }
    // The production Pages project's own host is publicly reachable and has
    // always served production; it is named explicitly rather than covered by
    // a *.pages.dev wildcard, because that wildcard is what let the non-prod
    // projects (dathent-ai-dev, dathent-ai-staging) resolve to production too.
    if (window.location.hostname === 'dathent-ai.pages.dev') {
      return 'https://api.dathent.ai';
    }
    // Other *.pages.dev preview hosts have no api.* counterpart to derive
    // from, so they can only be served by an explicit VITE_API_URL. Guessing
    // here is what caused the leak above; say so instead.
    if (window.location.hostname.endsWith('.pages.dev')) {
      console.error(
        '[dathent] No VITE_API_URL set for this preview host — the API base URL cannot be derived from *.pages.dev. Build with VITE_API_URL=https://api.<env>.dathent.ai.'
      );
    }
  }

  return 'http://localhost:8787';
}

export class DathentClient {
  constructor(options = {}) {
    this.baseURL = options.baseURL || defaultBaseURL();
    this.onAuthError = options.onAuthError || (() => {});
    this.token = null;
    // Headers merged into EVERY request. Used for cross-cutting concerns
    // like x-dathent-trace-id so a flow's frontend logs + worker logs
    // can be correlated. Set via setDefaultHeader / clearDefaultHeader.
    this._defaultHeaders = {};
    // GET response cache: key=method+url, value={ t: msExpire, v: data }
    // Optional per-call TTL via options.cacheTTL (ms). Cleared on mutating
    // calls via this.invalidateCache(prefix).
    this._cache = new Map();
    // In-flight GET dedupe: key=cache key, value=Promise. Concurrent calls
    // to the same URL return the same promise instead of re-fetching.
    this._inFlight = new Map();
    this._cacheVersions = new Map();
    this._cacheEpoch = 0;
    this._readOwnerEpoch = 0;
    this._readScopeListeners = new Set();

    // Initialize sub-clients
    this.auth = new AuthClient(this);
    this.profile = new ProfileClient(this);
    this.onboarding = new OnboardingClient(this);
    this.strategy = new StrategyClient(this);
    this.content = new ContentClient(this);
    this.scheduler = new SchedulerClient(this);
    this.socialAccounts = new SocialAccountsClient(this);
    this.autopilot = new AutopilotClient(this);
    this.notifications = new NotificationsClient(this);
    this.billing = new BillingClient(this);
    this.credits = new CreditsClient(this);
    this.apiKeys = new ApiKeysClient(this);
    this.platforms = new PlatformsClient(this);
    this.carousel = new CarouselClient(this);
    this.assistant = new AssistantClient(this);
    this.research = new ResearchClient(this);
    this.brandAssets = new BrandAssetsClient(this);
    this.analytics = new AnalyticsClient(this);
    this.threads = new ThreadsClient(this);
    this.instagram = new InstagramClient(this);
    this.referral = new ReferralClient(this);
    this.adminReferral = new AdminReferralClient(this);
    this.adminPreregs = new AdminPreregsClient(this);
    this.adminBroadcast = new AdminBroadcastClient(this);
    this.adminActivation = new AdminActivationClient(this);
    this.adminPlans = new AdminPlansClient(this);
    this.adminSupport = new AdminSupportClient(this);
    this.adminUsers = new AdminUsersClient(this);
    this.adminBehavior = new AdminBehaviorClient(this);
    this.adminReplay = new AdminReplayClient(this);
    this.adminCredits = new AdminCreditsClient(this);
    this.videos = new VideosClient(this);
    this.brands = new BrandsClient(this);
    // Active brand for multi-brand scoping. Set by BrandContext when the flag +
    // entitlement are on; null means single-brand (today's account-wide behavior),
    // so research/strategy calls send no brandProfileId.
    // Seed SYNCHRONOUSLY from the persisted value: screens fetch on mount
    // BEFORE BrandContext finishes hydrating, and an unscoped strategy call
    // falls back server-side to "most-recent brand" — which showed brand A's
    // plan under brand B's header. BrandContext reconciles with the server
    // right after and corrects this if stale.
    this._activeBrandId = null;
    try {
      // GA semantics (default ON): seed unless the emergency kill switch '0'
      // is set — must mirror flags.js multiBrandEnabled().
      if (typeof window !== 'undefined'
        && window.localStorage.getItem('dathent.multibrand.enabled') !== '0') {
        this._activeBrandId = readSeedActiveBrandId(window.localStorage);
      }
    } catch (_) { /* storage unavailable — BrandContext will set it */ }
  }

  setToken(token) {
    // CRITICAL: token change means user identity change (login/logout/switch
    // accounts). The GET response cache is keyed by URL only — not user —
    // so we MUST flush it on every token transition to avoid serving the
    // previous user's cached responses to the new session. Same for in-flight
    // dedupe map. This prevents cross-session data leaks (e.g. Dashboard
    // posts-summary, Library, analytics) when a user signs out and another
    // user signs in within the cache TTL window.
    const tokenChanged = this.token !== token;
    this.token = token;
    if (tokenChanged) {
      this.clearCache({ reset: true, reload: token !== null });
    }
    // Auth is cookie-based (HttpOnly session cookie set by auth-worker). We no
    // longer persist a bearer token in localStorage — it was a legacy fallback
    // and an unnecessary XSS token-theft surface. Proactively clear any stale
    // legacy token left over from the pre-cookie era.
    try { localStorage.removeItem('dathent_token'); } catch { /* private mode */ }
  }

  // Public helper for explicit cache flush (used by AuthContext on sign-in
  // and sign-out flows to ensure no stale data carries over).
  clearCache({ reset = false, reload = true } = {}) {
    this._cacheEpoch += 1;
    if (reset) this._readOwnerEpoch += 1;
    this._cache.clear();
    this._inFlight.clear();
    for (const listener of this._readScopeListeners) listener({ scope: this.getReadScope(), reset, reload });
  }

  // Opaque, credential-free scope stamp for consumers of async read results.
  getReadScope() {
    return `${this._cacheEpoch}:${this._activeBrandId || ''}`;
  }

  // Identity/brand ownership survives ordinary cache and billing refreshes.
  getReadOwnerScope() {
    return `${this._readOwnerEpoch}:${this._activeBrandId || ''}`;
  }

  subscribeReadScope(listener) {
    this._readScopeListeners.add(listener);
    return () => this._readScopeListeners.delete(listener);
  }

  getToken() {
    // In-memory only. Session auth rides on the HttpOnly cookie
    // (credentials:'include'); we no longer read a bearer token from
    // localStorage. Normally returns null in the cookie flow, so no
    // Authorization header is sent and the cookie is the sole credential.
    return this.token;
  }

  // Cross-cutting header helpers — used for x-dathent-trace-id during
  // onboarding activation so frontend logs and worker logs share an id.
  setDefaultHeader(name, value) {
    if (!name) return;
    if (value == null || value === '') {
      delete this._defaultHeaders[name];
    } else {
      this._defaultHeaders[name] = String(value);
    }
  }
  clearDefaultHeader(name) {
    if (name) delete this._defaultHeaders[name];
  }

  /* TASK-053 — upload with REAL byte progress.
     `fetch` cannot report upload progress (no request-side streams in browsers
     that ship this app), so any call site that wants a progress bar goes
     through XHR instead. Same auth posture as request(): cookie via
     withCredentials, bearer only if one is held in memory.
     Resolves the parsed JSON body; rejects with DathentAPIError like the rest
     of the client so callers handle one error shape. */
  uploadWithProgress(path, body, { method = 'POST', headers = {}, onProgress, signal } = {}) {
    const url = path.startsWith('http') ? path : `${this.baseURL}${path}`;
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(method, url, true);
      xhr.withCredentials = true;
      const token = this.getToken();
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      for (const [name, value] of Object.entries({ ...this._defaultHeaders, ...headers })) {
        if (value != null) xhr.setRequestHeader(name, String(value));
      }
      if (xhr.upload && typeof onProgress === 'function') {
        xhr.upload.addEventListener('progress', (event) => {
          // lengthComputable is false for chunked bodies — report bytes only,
          // let the caller decide how to render an unknown total.
          onProgress({
            loaded: event.loaded,
            total: event.lengthComputable ? event.total : null,
            ratio: event.lengthComputable && event.total > 0 ? event.loaded / event.total : null
          });
        });
      }
      if (signal) {
        if (signal.aborted) { xhr.abort(); reject(new DathentAPIError('Upload cancelled', 'CANCELLED', 0)); return; }
        signal.addEventListener('abort', () => xhr.abort(), { once: true });
      }
      xhr.addEventListener('abort', () => reject(new DathentAPIError('Upload cancelled', 'CANCELLED', 0)));
      xhr.addEventListener('error', () => reject(new DathentAPIError('Network error', 'NETWORK_ERROR', 0)));
      xhr.addEventListener('timeout', () => reject(new DathentAPIError('Upload timed out', 'TIMEOUT', 0)));
      xhr.addEventListener('load', () => {
        let payload = {};
        try { payload = xhr.responseText ? JSON.parse(xhr.responseText) : {}; }
        catch { payload = { message: (xhr.responseText || '').slice(0, 500) }; }
        if (xhr.status >= 200 && xhr.status < 300) {
          // Mirror request()'s cache invalidation so a fresh library list is
          // fetched after an upload, exactly as the fetch path does.
          resolve(payload);
        } else {
          if (xhr.status === 401) this.onAuthError();
          reject(new DathentAPIError(
            payload?.error?.message || payload?.message || `Upload failed (${xhr.status})`,
            payload?.error?.code || 'UPLOAD_FAILED',
            xhr.status,
            payload?.error?.details
          ));
        }
      });
      xhr.send(body);
    });
  }

  async request(method, path, data = null, options = {}) {
    const ownerEpoch = this._readOwnerEpoch;
    const url = `${this.baseURL}${path}`;
    const headers = {
      'Content-Type': 'application/json',
      ...this._defaultHeaders,
      ...options.headers,
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const config = {
      method,
      headers,
      credentials: options.credentials || 'include',
      // Never let the browser HTTP-cache authenticated API responses. The JS
      // _cache above handles intentional caching and is flushed on every auth
      // transition; without no-store a shared-URL GET could still be served
      // from disk cache across an account switch (cross-account data bleed).
      cache: 'no-store',
    };

    if (data && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
      config.body = JSON.stringify(data);
    }

    try {
      let response = await fetch(url, config);
      let didRateLimitRetry = false;
      let retryDelayMs = 0;

      if (response.status === 429 && options.retryRateLimit !== false) {
        retryDelayMs = await rateLimitRetryDelayMs(response.clone());
        if (retryDelayMs <= RATE_LIMIT_RETRY_MAX_DELAY_MS) {
          didRateLimitRetry = true;
          await sleep(retryDelayMs);
          response = await fetch(url, config);
        }
      }
      
      // Handle non-JSON responses
      const contentType = response.headers.get('content-type');
      let responseData;
      
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        responseData = { message: await response.text() };
      }

      if (!response.ok) {
        const apiError = responseData?.error || responseData || {};
        const errorMessage = typeof apiError === 'string'
          ? responseData?.message || apiError
          : apiError.message || responseData?.message || 'API request failed';
        const errorCode = typeof apiError === 'string'
          ? apiError
          : apiError.code || responseData?.code || 'UNKNOWN_ERROR';
        // Handle auth errors — but ONLY for Dathent's own session
        // endpoints. Third-party platform endpoints (Instagram, Threads,
        // Facebook, TikTok, X, Telegram) also surface 401 from the
        // PROVIDER side (e.g., expired Meta token, missing permission
        // pending App Review approval, revoked access on the user's IG
        // account). Those have NOTHING to do with the user's Dathent
        // session — auto-logging them out kicks the user back to /login
        // every time they open a post preview, which was the symptom
        // reported on /app/post/:id?platform=instagram. We treat such
        // 401s as plain API errors, not session-expired.
        // /api/connectors/* (OAuth-grant management) proxies to the MCP worker;
        // its 401/403s can come from the downstream internal trust gate (e.g. a
        // worker missing INTERNAL_SERVICE_SECRET), not from a dead session —
        // treating them as session-expired force-logged users out of Settings
        // (live incident 2026-07-21).
        // /oauth-mcp/* is the MCP OAuth authorization server (mcp-worker); its
        // status codes describe the OAuth request, never the app session. The
        // pending-authorize probe (TASK-217) runs right after sign-in — an
        // unexpected 401 there must not log the user straight back out.
        const isPlatformProxyEndpoint = /^\/(?:api\/)?(?:instagram|threads|facebook|tiktok|x|twitter|telegram|connectors)\//.test(path)
          || /^\/api\/social\/tiktok\//.test(path)
          || /^\/oauth-mcp\//.test(path);
        // A late response from a previous identity cannot expire a newer login.
        if (response.status === 401 && !isPlatformProxyEndpoint && ownerEpoch === this._readOwnerEpoch) {
          this.setToken(null);
          this.onAuthError();
        }

        const details = typeof apiError === 'string' ? responseData?.details || null : apiError.details || responseData?.details || null;
        const enrichedDetails = {
          ...(details && typeof details === 'object' && !Array.isArray(details) ? details : { details }),
          method,
          path,
          traceId: response.headers.get('x-trace-id') || null,
          retryAfterSeconds: retryAfterSeconds(response, details),
          retryAttempted: didRateLimitRetry,
          // A metrics refresh can persist some targets and still return 502.
          // Carry only the acknowledgement flags, never raw provider diagnostics.
          ...(/^\/api\/content\/[^/]+\/metrics\/refresh$/.test(path) && responseData?.data ? {
            metricRefresh: {
              refreshed: responseData.data.refreshed === true,
              complete: responseData.data.complete === true,
            },
          } : {}),
        };

        throw new DathentAPIError(
          errorMessage,
          errorCode,
          response.status,
          enrichedDetails
        );
      }

      return responseData?.ok === true && Object.prototype.hasOwnProperty.call(responseData, 'data')
        ? responseData.data
        : responseData;
    } catch (error) {
      if (error instanceof DathentAPIError) {
        throw error;
      }
      
      // Network or other errors
      throw new DathentAPIError(
        error.message || 'Network error',
        'NETWORK_ERROR',
        0,
        { originalError: error }
      );
    }
  }

  // Cached + deduplicated GET. Pass options.cacheTTL (ms) to enable caching;
  // omit it for a plain request. Concurrent calls with the same key share
  // a single in-flight promise so a fast navigation burst doesn't fan out.
  get(path, options) {
    const ttl = options?.cacheTTL;
    const key = `GET ${path}`;
    const now = Date.now();
    const epoch = this._cacheEpoch;
    const version = this._cacheVersions.get(key) || 0;
    if (ttl > 0) {
      const cached = this._cache.get(key);
      if (cached && cached.t > now) return Promise.resolve(cached.v);
    }
    if (options?.dedupe !== false && this._inFlight.has(key)) return this._inFlight.get(key);
    const promise = this.request('GET', path, null, options)
      .then((value) => {
        if (ttl > 0 && epoch === this._cacheEpoch && version === (this._cacheVersions.get(key) || 0)) {
          this._cache.set(key, { t: now + ttl, v: value });
        }
        if (this._inFlight.get(key) === promise) this._inFlight.delete(key);
        return value;
      })
      .catch((err) => {
        if (this._inFlight.get(key) === promise) this._inFlight.delete(key);
        throw err;
      });
    if (options?.dedupe !== false) this._inFlight.set(key, promise);
    return promise;
  }

  // Mutating verbs invalidate any cached GETs whose URL begins with the
  // supplied prefix(es). Sub-clients can also call invalidateCache directly
  // after any custom action.
  post(path, data, options) {
    return this.request('POST', path, data, options).finally(() => {
      this.invalidateCache(options?.invalidate ?? path);
    });
  }
  put(path, data, options) {
    return this.request('PUT', path, data, options).finally(() => {
      this.invalidateCache(options?.invalidate ?? path);
    });
  }
  patch(path, data, options) {
    return this.request('PATCH', path, data, options).finally(() => {
      this.invalidateCache(options?.invalidate ?? path);
    });
  }
  delete(path, options) {
    return this.request('DELETE', path, null, options).finally(() => {
      this.invalidateCache(options?.invalidate ?? path);
    });
  }

  // Multi-brand: BrandContext sets the active brand here so research/strategy
  // calls scope to it without every call site threading the id. Null → no
  // brandProfileId is sent (today's account-wide behavior).
  setActiveBrandId(id) {
    const next = id || null;
    if (this._activeBrandId === next) return;
    this._activeBrandId = next;
    this.clearCache({ reset: true });
  }
  getActiveBrandId() {
    return this._activeBrandId;
  }
  // Merge the active brand into a request body. An explicit brandProfileId in
  // `data` wins (spread last).
  _withBrand(data = {}) {
    return this._activeBrandId ? { brandProfileId: this._activeBrandId, ...data } : data;
  }
  // "?brandProfileId=…" for GET list calls, or "" when no active brand.
  _brandQuery(prefix = '?') {
    return this._activeBrandId
      ? `${prefix}brandProfileId=${encodeURIComponent(this._activeBrandId)}`
      : '';
  }

  invalidateCache(prefixes) {
    if (!prefixes) return;
    const list = Array.isArray(prefixes) ? prefixes : [prefixes];
    for (const prefix of list) {
      // Normalise: strip query, drop leading slash differences
      const norm = String(prefix).split('?')[0];
      const keys = new Set([...this._cache.keys(), ...this._inFlight.keys(), ...this._cacheVersions.keys()]);
      for (const key of keys) {
        // key is "GET /api/whatever"
        if (key.startsWith(`GET ${norm}`)) {
          this._cache.delete(key);
          this._inFlight.delete(key);
          this._cacheVersions.set(key, (this._cacheVersions.get(key) || 0) + 1);
        }
      }
    }
  }

  url(path) {
    return `${this.baseURL}${path}`;
  }
}

function retryAfterSeconds(response, details) {
  const header = Number(response.headers.get('Retry-After'));
  if (Number.isFinite(header) && header >= 0) return header;
  const detailValue = Number(details?.retryAfterSeconds);
  return Number.isFinite(detailValue) && detailValue >= 0 ? detailValue : null;
}

async function rateLimitRetryDelayMs(response) {
  let details = null;
  try {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const body = await response.json();
      details = body?.error?.details || body?.details || null;
    }
  } catch {
    details = null;
  }
  const seconds = retryAfterSeconds(response, details);
  if (seconds === null) return 750;
  return Math.max(250, Math.ceil(seconds * 1000));
}

function sleep(ms) {
  return new Promise(resolve => globalThis.setTimeout(resolve, ms));
}

/* Read a File/Blob and return its body as a base64 string (no data: prefix).
   Used for the JSON-based image upload endpoint — multipart is awkward
   client-side and base64 keeps the request shape consistent with the rest
   of the API. */
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/* Best-effort image dimensions read (decodes via an Image element). The
   server can usually figure dims out itself, but passing them lets us
   skip a roundtrip and surface dimension errors before the upload. */
function readImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const out = { width: img.naturalWidth || 0, height: img.naturalHeight || 0 };
      URL.revokeObjectURL(url);
      resolve(out);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not decode image'));
    };
    img.src = url;
  });
}

// Auth Client
class AuthClient {
  constructor(client) {
    this.client = client;
  }

  async login(email, password) {
    const response = await this.client.post('/api/auth/login', { email, password });
    if (response.requiresMfa || response.requiresMfaSetup) {
      return response;
    }
    if (response.token) {
      this.client.setToken(response.token);
    }
    return response.user;
  }

  async verifyRequiredMfaSetup(attemptId, code) {
    const response = await this.client.post('/api/auth/mfa/totp/login/setup/verify', { attemptId, code });
    if (response.token) {
      this.client.setToken(response.token);
    }
    return response;
  }

  async verifyMfaLogin(attemptId, code) {
    const response = await this.client.post('/api/auth/mfa/totp/login/verify', { attemptId, code });
    if (response.token) {
      this.client.setToken(response.token);
    }
    return response.user;
  }

  async verifyMfaBackupCode(attemptId, backupCode) {
    const response = await this.client.post('/api/auth/mfa/backup-code/login/verify', { attemptId, backupCode });
    if (response.token) {
      this.client.setToken(response.token);
    }
    return response.user;
  }

  async changePassword({ currentPassword, newPassword }) {
    // currentPassword is optional — a passwordless (Google) account setting one
    // for the first time omits it; the server requires it only when a password
    // already exists.
    return this.client.post('/api/auth/password/change', { currentPassword, newPassword });
  }

  async sendMfaEmailCode(attemptId) {
    return this.client.post('/api/auth/mfa/email/login/send', { attemptId });
  }

  async verifyMfaEmailCode(attemptId, code) {
    const response = await this.client.post('/api/auth/mfa/email/login/verify', { attemptId, code });
    if (response.token) {
      this.client.setToken(response.token);
    }
    return response.user;
  }

  async getMfaStatus() {
    return this.client.get('/api/auth/mfa/status');
  }

  async startMfaSetup() {
    return this.client.post('/api/auth/mfa/totp/setup/start');
  }

  async verifyMfaSetup(code) {
    return this.client.post('/api/auth/mfa/totp/setup/verify', { code });
  }

  async disableMfa(password, code) {
    return this.client.post('/api/auth/mfa/totp/disable', { password, code });
  }

  async regenerateMfaBackupCodes(password, code) {
    return this.client.post('/api/auth/mfa/backup-codes/regenerate', { password, code });
  }

  async signup(email, password, name, turnstileToken, next) {
    // TASK-070: attach "where did this user come from" (referrer/UTM of the
    // converting visit + ux session id) — analytics-only, sanitized server-side.
    let attribution;
    try {
      attribution = getUxAttribution();
    } catch { attribution = undefined; }
    const response = await this.client.post('/api/auth/signup', { email, password, name, turnstileToken, attribution, ...(next ? { next } : {}) });
    if (response.requiresMfaSetup) {
      return response;
    }
    if (response.token) {
      this.client.setToken(response.token);
    }
    return response.user;
  }

  async logout() {
    try {
      await this.client.post('/api/auth/logout');
    } finally {
      this.client.setToken(null);
    }
  }

  async getCurrentUser() {
    // Short TTL (matches the app's other identity-adjacent reads) — this
    // endpoint was one of the hottest polling targets tripping the
    // Cloudflare per-IP rate limit, so a brief cache absorbs bursty
    // re-fetches (mount races, focus refetches) without staling auth state.
    const response = await this.client.get('/api/auth/me', { cacheTTL: 15 * 1000 });
    // The wire shape is `{ user, sessionId, organizationId, isAdmin,
    // bypassOnboarding }`. The app consumes a flat user object everywhere —
    // attach `isAdmin` and `bypassOnboarding` onto it so callers can read
    // `user.isAdmin` / `user.bypassOnboarding` without a separate API call.
    // `bypassOnboarding` flags Meta/Threads/Instagram review test accounts
    // (server-side allowlist via REVIEW_BYPASS_EMAILS) so they skip the
    // onboarding wizard and land straight on the dashboard.
    if (response?.user) {
      return {
        ...response.user,
        isAdmin: response.isAdmin === true,
        bypassOnboarding: response.bypassOnboarding === true,
        // ISO timestamp when the user requested account deletion, or null.
        // Drives the "scheduled for deletion / cancel" banner in Settings.
        deletionRequestedAt: response.deletionRequestedAt ?? null,
        // Whether the email is confirmed. Kept RAW (true/false/undefined): the
        // SPA wall only triggers on an explicit `=== false`, so an older server
        // that omits the field never produces a false lockout.
        emailVerified: response.emailVerified,
        // TASK-325: null for a solo/no-org account. Settings' WorkspaceTab
        // uses this to hide the "Save post visuals to Media library" toggle
        // (solo accounts are always ON server-side — nothing to configure).
        organizationId: response.organizationId ?? null,
        // ChatGPT-skip-onboarding (2026-09-10): raw users.signup_source
        // column ('chatgpt' | 'shopify' | 'web' | null — never derived
        // client-side). lib/signupSource.js's isChatGptOrigin() reads this as
        // the authoritative signal; an older server that omits the field
        // falls back to the existing localStorage/pendingOAuth heuristic.
        signupSource: response.signupSource ?? null,
      };
    }
    return response;
  }

  // Self-service account deletion (30-day reversible grace, then purge).
  async requestAccountDeletion() {
    return this.client.post('/api/auth/account/request-deletion', {});
  }

  // TASK-217 (issue #335): "is an MCP OAuth consent parked for this browser?"
  // mcp-worker answers { pending: false } | { pending: true, resumeUrl } from
  // its __Host-mcp-oauth-pending cookie — credentials:'include' carries it
  // (app.<env> and api.<env> are same-site, so SameSite=Lax is fine) and the
  // gateway passes /oauth-mcp/* through to mcp-worker with cookies intact.
  // Never cached (no cacheTTL, request() already sends cache:'no-store'): the
  // answer flips the moment consent is shown or approved.
  async getPendingOAuth({ connector = 'chatgpt' } = {}) {
    return this.client.get(connector === 'claude' ? '/oauth-mcp/claude/pending' : '/oauth-mcp/pending');
  }

  async cancelAccountDeletion() {
    return this.client.post('/api/auth/account/cancel-deletion', {});
  }

  async refreshToken() {
    const response = await this.client.post('/api/auth/refresh');
    if (response.token) {
      this.client.setToken(response.token);
    }
    return response;
  }

  async forgotPassword(email) {
    return this.client.post('/api/auth/password/forgot', { email });
  }

  async resetPassword(token, password) {
    return this.client.post('/api/auth/password/reset', { token, password });
  }

  // Consume an email-confirmation link (token from the email). Unauthenticated —
  // the token is the proof. Idempotent server-side.
  async verifyEmail(token) {
    return this.client.post('/api/auth/verify', { token });
  }

  // Reissue a confirmation link for the logged-in user sitting on the wall.
  async resendVerification() {
    return this.client.post('/api/auth/verify/resend', {});
  }

  async startFreePreview() {
    return this.client.post('/api/auth/free-preview/start', {}, {
      invalidate: ['/api/billing/entitlements'],
    });
  }
}

// Profile Client
class ProfileClient {
  constructor(client) {
    this.client = client;
  }

  async getBrandProfile({ fresh = false } = {}) {
    return this.client.get('/api/brand-profile', fresh ? { dedupe: false } : { cacheTTL: 5 * 60 * 1000 });
  }

  async updateBrandProfile(data) {
    return this.client.put('/api/brand-profile', data);
  }

  async addBrandReference(data) {
    return this.client.post('/api/brand-profile/references', data);
  }

  async getPublishingPreferences({ fresh = false } = {}) {
    return this.client.get('/api/profile/publishing-preferences', fresh ? { dedupe: false } : { cacheTTL: 2 * 60 * 1000 });
  }

  async updatePublishingPreferences(data) {
    return this.client.put('/api/profile/publishing-preferences', data);
  }

  // TASK-325: per-organization "Save post visuals to Media library" toggle.
  // Same cache/invalidate shape as publishing-preferences above — put()
  // invalidates this exact path by default, so a toggle is immediately
  // reflected on the next read (Settings and BrandKitScreen never see a
  // stale 2-minute-old value right after the user flips it).
  async getOrgSettings() {
    return this.client.get('/api/profile/org-settings', { cacheTTL: 2 * 60 * 1000 });
  }

  async updateOrgSettings(data) {
    return this.client.put('/api/profile/org-settings', data);
  }

  async getPlatformPreferences() {
    return this.client.get('/api/profile/platform-preferences', { cacheTTL: 60 * 1000 });
  }

  async updatePlatformPreference(data) {
    return this.client.put('/api/profile/platform-preferences', data);
  }

  async rebuildStrategy() {
    return this.client.post('/api/profile/rebuild-strategy');
  }
}

// Multi-brand — brands list / add-brand / switch active brand. Backed by the
// profile-worker /internal/profile/brands* endpoints (gateway /api/profile/brands*).
class BrandsClient {
  constructor(client) {
    this.client = client;
  }

  // { brands: [{ id, name, logoUrl, monogram, hasStrategy, hasResearch }],
  //   activeBrandId, brandsLimit }  (brandsLimit null = unlimited)
  async list() {
    return this.client.get('/api/profile/brands', { cacheTTL: 60 * 1000 });
  }

  // Create a new brand (name/website/description). 402 when at brand_profiles_total.
  async create(data = {}) {
    return this.client.post('/api/profile/brands', data, { invalidate: ['/api/profile/brands'] });
  }

  // Switch the active brand — invalidates every brand-scoped surface so it refetches.
  async setActive(brandProfileId) {
    return this.client.put('/api/profile/brands/active', { brandProfileId }, {
      invalidate: ['/api/profile/brands', '/api/research', '/api/strategy', '/api/brand-profile'],
    });
  }
}

// Onboarding Client
class OnboardingClient {
  constructor(client) {
    this.client = client;
  }

  async start() {
    return this.client.post('/api/onboarding/start');
  }

  async saveMessage({ stepKey, question = null, answerText, nextStep = null, extracted = null }) {
    return this.client.post('/api/onboarding/message', { stepKey, question, answerText, nextStep, extracted });
  }

  async sendAiMessage({ answerText, currentStep, clientTurnId = null, uiLanguage = null }) {
    return this.client.post('/api/onboarding/ai/message', { answerText, currentStep, clientTurnId, uiLanguage });
  }

  async analyzeWebsite({ url }) {
    return this.client.post('/api/onboarding/website/analyze', { url });
  }

  // Stage G — optional, confirm-first brand auto-detect. Returns logo/color
  // candidates pulled from the user's website; writes nothing server-side
  // (persistence happens only on explicit confirm via addReference).
  async detectBrand({ url, brandName }) {
    return this.client.post('/api/onboarding/detect-brand', { url, brandName });
  }

  async addReference(data) {
    return this.client.post('/api/onboarding/references', data);
  }

  async complete(data = {}) {
    return this.client.post('/api/onboarding/complete', data);
  }

  async createPersonalWorkspace() {
    return this.client.post('/api/onboarding/personal-workspace', {}, {
      invalidate: ['/api/brand-profile', '/api/profile/brands', '/api/onboarding/status', '/api/billing/entitlements'],
    });
  }

  async getStatus() {
    return this.client.get('/api/onboarding/status');
  }
}

// Strategy Client
class StrategyClient {
  constructor(client) {
    this.client = client;
  }

  // Every generate invalidates the cached GET — without this the post-regen
  // re-fetch served the 5-minute-old plan and the screen looked frozen until
  // a manual reload.
  async generate(data = {}) {
    return this.client.post('/api/strategy/generate', this.client._withBrand(data), { invalidate: ['/api/strategy'] });
  }

  // Durable regenerate: returns { operationId } (202) and runs in the content
  // worker's queue, so it survives a reload and shows in the Activity feed. The
  // SPA tracks the op via GenerationStatusContext and re-fetches the plan on
  // completion. (The synchronous generate() above stays for other callers.)
  async regenerate() {
    return this.client.post('/api/content/plan/regenerate', {}, { invalidate: ['/api/strategy'] });
  }

  async get(params = {}) {
    const query = new URLSearchParams(this.client._withBrand(params)).toString();
    return this.client.get(`/api/strategy${query ? `?${query}` : ''}`, { cacheTTL: 5 * 60 * 1000 });
  }

  // Force a fresh, uncached read — used right after a durable regenerate
  // completes, otherwise the 5-min cache (possibly re-warmed with the OLD plan
  // during the rebuild) serves stale data and the screen looks like nothing
  // changed until a few reloads later.
  async getFresh() {
    this.client.invalidateCache(['/api/strategy']);
    return this.client.get(`/api/strategy${this.client._brandQuery()}`, { cacheTTL: 0 });
  }

  async generateWeeklyPlan(data) {
    return this.client.post('/api/strategy/weekly-plan', this.client._withBrand(data), { invalidate: ['/api/strategy'] });
  }

  async generateMonthlyPlan(data = {}) {
    return this.client.post('/api/strategy/monthly-plan', this.client._withBrand(data), { invalidate: ['/api/strategy'] });
  }
}

// Content Client
class ContentClient {
  constructor(client) {
    this.client = client;
  }

  async generate(params) {
    return this.client.post('/api/content/generate', this.client._withBrand(params));
  }

  async generateFirstPost(params = {}) {
    return this.client.post('/api/content/first-post', this.client._withBrand(params));
  }

  /* GH-1133 S5 (design §2.0 C3): the free-preview board's only write — ask
     content-worker to complete the preview pack (drafts 2–3). Idempotent and
     asynchronous: 202 {queued|inProgress}, 200 {complete|brand_incomplete|
     eligible:false}; 404 = preview off, 409 = research still running. The
     gateway opens this one route for a granted preview-arm org. */
  async previewDrafts(data = {}) {
    return this.client.post('/api/content/preview/drafts', data, { invalidate: ['/api/content'] });
  }

  async createBatch(params) {
    return this.client.post('/api/content/batches', this.client._withBrand(params));
  }

  // "Improve with AI" on a user-written caption. mode ∈ tighten|punchier|grammar|hook|hashtags.
  // Returns { suggestion, mode }; metered by caption_assists_monthly.
  async improveCaption({ text, mode, platforms } = {}) {
    return this.client.post('/api/content/caption/improve', { text, mode, platforms });
  }

  async listBatches(filters = {}) {
    const query = new URLSearchParams(filters).toString();
    return this.client.get(`/api/content/batches${query ? `?${query}` : ''}${this.client._brandQuery(query ? '&' : '?')}`, { cacheTTL: 30 * 1000 });
  }

  async getBatch(id) {
    return this.client.get(`/api/content/batches/${id}`);
  }

  async appendBatch(id, data = {}) {
    return this.client.post(`/api/content/batches/${id}/append`, data);
  }

  async archiveBatch(id) {
    return this.client.post(`/api/content/batches/${id}/archive`);
  }

  async pauseBatch(id) {
    return this.client.post(`/api/content/batches/${id}/pause`);
  }

  async rescheduleBatch(id, data = {}) {
    return this.client.post(`/api/content/batches/${id}/reschedule`, data);
  }

  async list(filters = {}) {
    const query = new URLSearchParams(filters).toString();
    return this.client.get(`/api/content${query ? `?${query}` : ''}${this.client._brandQuery(query ? '&' : '?')}`);
  }

  async feed(filters = {}) {
    const query = new URLSearchParams(filters).toString();
    return this.client.get(`/api/content/feed${query ? `?${query}` : ''}${this.client._brandQuery(query ? '&' : '?')}`);
  }

  async get(id, options = {}) {
    // 30s cache — PostPreview + EditPost frequently fetch the same content.
    // options.fresh bypasses the cache so the image-regen poller sees the new
    // media the instant the server attaches it (a cached read would keep
    // returning the old image for up to 30s).
    const targetQuery = options.targetId ? `?targetId=${encodeURIComponent(options.targetId)}` : '';
    return this.client.get(`/api/content/${encodeURIComponent(id)}${targetQuery}`, { cacheTTL: options.fresh ? 0 : 30 * 1000 });
  }

  async createManualDraft(data) {
    return this.client.post('/api/content/manual-drafts', data, { invalidate: ['/api/content'] });
  }

  async updateManualDraft(id, data) {
    return this.client.put(`/api/content/${encodeURIComponent(id)}/manual-draft`, data, { invalidate: ['/api/content'] });
  }

  /* Server-tracked in-progress (and just-finished) async operations for the
     global Activity feed. No cache — it's polled and must reflect live server
     state so the user sees ongoing work from anywhere, across reloads. */
  async activeOperations() {
    return this.client.get('/api/content/operations/active');
  }

  async refreshMetrics(id, data = {}) {
    return this.client.post(`/api/content/${encodeURIComponent(id)}/metrics/refresh`, data);
  }

  async adaptPlatforms(id, data = {}) {
    return this.client.post(`/api/content/${id}/adapt-platforms`, data);
  }

  async regenerate(id, data = {}) {
    return this.client.post(`/api/content/${id}/regenerate`, data, {
      invalidate: [`/api/content/${encodeURIComponent(id)}`, `/api/content`],
    });
  }

  /* Image-only regeneration. Keeps caption/copy, replaces carousel
     slides with freshly-generated images using the post's existing
     prompt + language. Returns the refreshed content payload.
     Optional fields the backend accepts:
       instructions  — user's free-text note on what to change
       language      — ISO override (e.g. "lt") for image text
       slideIndex    — 1-based: regenerate only ONE slide of a carousel
     All three are passed through to the visual prompt.
   */
  async regenerateImage(id, data = {}) {
    return this.client.post(`/api/content/${encodeURIComponent(id)}/regenerate-image`, data, {
      invalidate: [`/api/content/${encodeURIComponent(id)}`, `/api/content`],
    });
  }

  /* Persist a slide reorder for a content item's active version. Frontend
     uses this on drag-drop autosave — there's no Save button on the media
     tab; every reorder gesture fires one POST. */
  async reorderSlides(id, slideIds) {
    return this.client.post(`/api/content/${encodeURIComponent(id)}/slides/reorder`, { slideIds }, {
      invalidate: [`/api/content/${encodeURIComponent(id)}`],
    });
  }

  /* Upload a user-supplied image and bind it to a carousel slide.
     `file` is a File / Blob; we read it as base64 client-side so the
     request is plain JSON (multipart is awkward to construct from fetch).
     `slideIndex` is 1-based — the slide to REPLACE. EditPost passes
     the currently-selected slide so the upload lands where the user
     expects, not always slide 1. */
  async uploadCustomImage(id, file, { slideIndex = 1, onProgress } = {}) {
    const base64 = await fileToBase64(file);
    const dims = await readImageDimensions(file).catch(() => ({ width: 0, height: 0 }));
    const body = {
      imageBase64: base64,
      width: dims.width,
      height: dims.height,
      slideIndex
    };
    // TASK-053: with onProgress take the XHR path for real byte progress;
    // without it this is byte-identical to the previous fetch call.
    if (typeof onProgress === 'function') {
      const result = await this.client.uploadWithProgress(`/api/content/${encodeURIComponent(id)}/upload-image`, JSON.stringify(body), {
        headers: { 'Content-Type': 'application/json' },
        onProgress
      });
      this.client.invalidateCache([`/api/content/${id}`, '/api/content']);
      return result;
    }
    return this.client.post(`/api/content/${encodeURIComponent(id)}/upload-image`, body, {
      invalidate: [`/api/content/${id}`, `/api/content`],
    });
  }

  /* TASK-039/052 — attach or replace Media-library images on an EXISTING post
     without recreating it. `mode` 'attach' appends after current media,
     'replace' swaps all of it. The post's schedule is untouched, and a post
     held by the no-visuals gate is released server-side once media lands. */
  async attachMedia(id, mediaAssetIds, { mode = 'attach' } = {}) {
    return this.client.post(`/api/content/${encodeURIComponent(id)}/media`, {
      mediaAssetIds,
      mode
    }, {
      invalidate: [`/api/content/${id}`, `/api/content`],
    });
  }

  /* Delete a single carousel slide from a content item's active version.
     Backend re-packs remaining slides' indices back to 1..N. Refuses to
     delete the last slide on image-required formats — surface that error
     to the user via the standard error path. */
  async deleteSlide(id, slideId) {
    return this.client.delete(`/api/content/${encodeURIComponent(id)}/slides/${encodeURIComponent(slideId)}`, {
      invalidate: [`/api/content/${id}`, `/api/content`],
    });
  }

  /* TASK-074 — release a HELD post (text ready, visual failed, no media) for
     text-only publishing. Sets content_items.allow_text_only=1 server-side so
     the publish gate lets the post go out without an image. Backend contract
     (built in parallel): POST /api/content/items/:id/allow-text-only via the
     gateway content route. Invalidate both the per-item GET and the feed so
     the "Waiting for image" state clears everywhere on the next fetch. */
  async postAllowTextOnly(id) {
    return this.client.post(`/api/content/items/${encodeURIComponent(id)}/allow-text-only`, {}, {
      invalidate: [`/api/content/${encodeURIComponent(id)}`, `/api/content`],
    });
  }

  async listVersions(id) {
    return this.client.get(`/api/content/${id}/versions`);
  }

  async activateVersion(id, versionId) {
    return this.client.post(`/api/content/${id}/versions/${versionId}/activate`);
  }

  async createManualEdit(id, data) {
    return this.client.post(`/api/content/${id}/manual-edit`, data);
  }

  async archive(id) {
    return this.client.post(`/api/content/${id}/archive`);
  }

  async restore(id) {
    return this.client.post(`/api/content/${id}/restore`);
  }

  async duplicate(id) {
    return this.client.post(`/api/content/${id}/duplicate`);
  }
}

// Scheduler Client
//
// Every mutation here invalidates BOTH the scheduler cache (its own path)
// AND the content cache. This is critical: scheduler operations write to
// `content_items.status` and the publishSummary that PostPreview/Library
// render — without invalidating `/api/content`, the 30s GET cache on
// `api.content.get(id)` hands back the pre-schedule snapshot and the
// PostPreview status pill stays "Ready" instead of flipping to
// "Scheduled". Same applies to reschedule, clone, unschedule, delete,
// changeTargets, and retry.
//
// The invalidate prefix `/api/content` is intentionally broad — it
// covers both the per-item GET (`/api/content/<id>`) and the feed
// (`/api/content/feed`), so Library + Calendar + Dashboard widgets also
// see fresh data on the next fetch.
class SchedulerClient {
  constructor(client) {
    this.client = client;
  }

  async list(filters = {}) {
    const query = new URLSearchParams(filters).toString();
    return this.client.get(`/api/scheduler/posts${query ? `?${query}` : ''}${this.client._brandQuery(query ? '&' : '?')}`);
  }

  async create(data) {
    const result = await this.client.post('/api/scheduler/posts', data, {
      invalidate: ['/api/scheduler', '/api/content'],
    });
    // Funnel (TASK-122): first_publish must cover EVERY publish path — the
    // six create/clone call sites (onboarding, Library, EditPost) all funnel
    // through this one client method, so the once-per-user event lives here
    // rather than in each screen. trackOnce no-ops while the flag is off.
    try { trackOnce('first_publish', { surface: 'scheduler_create' }); } catch (_) { /* fail-silent */ }
    return result;
  }

  async getCalendar(startDate, endDate, filters = {}) {
    const query = new URLSearchParams({ from: startDate, to: endDate, ...filters }).toString();
    // 30s cache — Calendar + Dashboard WeekStrip + ContentPlan all hit this
    return this.client.get(`/api/scheduler/calendar?${query}${this.client._brandQuery('&')}`, { cacheTTL: 30 * 1000 });
  }

  async aggregateStatus(id) {
    return this.client.post(`/api/scheduler/posts/${id}/status`, undefined, {
      invalidate: ['/api/scheduler', '/api/content'],
    });
  }

  async update(id, data) {
    return this.client.patch(`/api/scheduler/posts/${id}`, data, {
      invalidate: ['/api/scheduler', '/api/content'],
    });
  }

  async reschedule(id, data) {
    return this.client.post(`/api/scheduler/posts/${id}/reschedule`, data, {
      invalidate: ['/api/scheduler', '/api/content'],
    });
  }

  async unschedule(id) {
    return this.client.post(`/api/scheduler/posts/${id}/unschedule`, undefined, {
      invalidate: ['/api/scheduler', '/api/content'],
    });
  }

  async changeTargets(id, data) {
    return this.client.put(`/api/scheduler/posts/${id}/targets`, data, {
      invalidate: ['/api/scheduler', '/api/content'],
    });
  }

  async retry(id, data = {}) {
    return this.client.post(`/api/scheduler/posts/${id}/retry`, data, {
      invalidate: ['/api/scheduler', '/api/content'],
    });
  }

  async clone(id, data = {}) {
    const result = await this.client.post(`/api/scheduler/posts/${id}/clone`, data, {
      invalidate: ['/api/scheduler', '/api/content'],
    });
    // Funnel (TASK-122): same once-per-user first_publish as create() above —
    // EditPost's re-publish path goes through clone, not create.
    try { trackOnce('first_publish', { surface: 'scheduler_clone' }); } catch (_) { /* fail-silent */ }
    return result;
  }

  async linkRecovery(data) {
    return this.client.post('/api/scheduler/targets/recoveries', data, {
      invalidate: ['/api/scheduler', '/api/content'],
    });
  }

  async delete(id) {
    return this.client.delete(`/api/scheduler/posts/${id}`, {
      invalidate: ['/api/scheduler', '/api/content'],
    });
  }
}

// Social Accounts Client
class SocialAccountsClient {
  constructor(client) {
    this.client = client;
  }

  async list({ brandProfileId } = {}) {
    // Multi-brand: accounts are per-brand (owner decision) — scope the list to
    // the active brand. Inert until the auth-worker filter deploys; no active
    // brand → unscoped (legacy).
    const query = brandProfileId ? `?brandProfileId=${encodeURIComponent(brandProfileId)}` : this.client._brandQuery();
    return this.client.get(`/social/accounts${query}`, { cacheTTL: 2 * 60 * 1000 });
  }

  async connect(platform, options = {}) {
    if (platform === 'instagram') {
      window.location.assign(this.getInstagramOAuthStartUrl(options));
      return { redirected: true };
    }
    if (platform === 'threads') {
      window.location.assign(this.getThreadsOAuthStartUrl(options));
      return { redirected: true };
    }
    if (platform === 'facebook') {
      // Facebook Page connect only. The legacy 'meta' alias (which mapped to
      // Instagram-via-Facebook) was removed — Instagram uses direct login above.
      window.location.assign(this.getMetaOAuthStartUrl('facebook', options));
      return { redirected: true };
    }
    if (platform === 'tiktok') {
      window.location.assign(this.getTikTokOAuthStartUrl(options));
      return { redirected: true };
    }
    if (platform === 'x' || platform === 'twitter') {
      window.location.assign(this.getXOAuthStartUrl(options));
      return { redirected: true };
    }
    if (platform === 'linkedin') {
      window.location.assign(this.getLinkedInOAuthStartUrl(options));
      return { redirected: true };
    }
    if (platform === 'telegram') {
      return this.createTelegramLink();
    }
    return this.client.post('/social/connect', { platform });
  }

  // Multi-brand: the OAuth start is the ONE brand-sensitive call that leaves
  // the SPA entirely (full-page redirect), so the active brand must ride the
  // URL explicitly. Every start handler already prefers ?brandProfileId= over
  // its server-side user_active_brand fallback — but that fallback can lag the
  // SPA's own switcher (owner connect 2026-08-11 02:46 landed on the previous
  // brand; user_active_brand only caught up at 02:51), which stamped the new
  // account onto a brand the user wasn't looking at and the per-brand accounts
  // list rendered empty. No active brand → params unchanged (legacy behavior).
  _oauthParams(options = {}) {
    const params = oauthReturnParams(options);
    if (this.client._activeBrandId) params.brandProfileId = this.client._activeBrandId;
    return params;
  }

  // connectInstagramViaFacebook() was removed — Instagram connects via direct
  // Instagram Login (connect('instagram') → /oauth/instagram/start). getMetaOAuthStartUrl
  // stays for the independent Facebook Page connect (connect('facebook')).
  getMetaOAuthStartUrl(platform = 'facebook', options = {}) {
    return this.client.url(addQuery('/oauth/meta/start', { platform, ...this._oauthParams(options), ...(platform === 'facebook' && options.metrics === true ? { metrics: 1 } : {}) }));
  }

  getInstagramOAuthStartUrl(options = {}) {
    return this.client.url(addQuery('/oauth/instagram/start', this._oauthParams(options)));
  }

  getThreadsOAuthStartUrl(options = {}) {
    return this.client.url(addQuery('/oauth/threads/start', this._oauthParams(options)));
  }

  getTikTokOAuthStartUrl(options = {}) {
    // GH-794: a metrics consent names its account so auth-worker can refuse a
    // grant made while TikTok is signed in as a different account.
    return this.client.url(addQuery('/oauth/tiktok/start', { ...this._oauthParams(options), ...(options.metrics === true ? { metrics: 1, account: options.accountId } : {}) }));
  }

  getXOAuthStartUrl(options = {}) {
    return this.client.url(addQuery('/oauth/x/start', this._oauthParams(options)));
  }

  getLinkedInOAuthStartUrl(options = {}) {
    return this.client.url(addQuery('/oauth/linkedin/start', { ...this._oauthParams(options), ...(options.metrics === true ? { metrics: 1 } : {}) }));
  }

  async createTelegramLink() {
    // Same explicit-brand rule as the OAuth starts: the handler reads
    // ?brandProfileId= (not the body) before falling back to user_active_brand.
    return this.client.post(`/social/telegram/link${this.client._brandQuery()}`, {});
  }

  async disconnect(id) {
    // Disconnect a single account by id, but invalidate the shared accounts
    // cache (`/social/accounts`) so the next list() returns fresh data.
    return this.client.delete(`/social/${id}`, { invalidate: ['/social/accounts', `/social/${id}`] });
  }

  // Update a single account's settings (currently just publishLanguage).
  // Backend gates per-account language override behind entitlement
  // per_account_language_override (Growth+). Lower plans get 402
  // ENTITLEMENT_REQUIRED with upgradeUrl; the caller should show an upgrade CTA.
  async updateLanguage(id, language) {
    return this.client.request(
      'PATCH',
      `/api/social/accounts/${encodeURIComponent(id)}`,
      { publishLanguage: language },
      { invalidate: ['/social/accounts', `/social/${id}`] },
    );
  }

  async refresh(id) {
    return this.client.post(`/social/${id}/refresh`, null, {
      invalidate: ['/social/accounts', `/social/${id}`],
    });
  }

  async healthCheck(id) {
    return this.client.get(`/social/${id}/health`);
  }

  async getTikTokCreatorInfo(accountId) {
    return this.client.get(`/api/social/tiktok/creator-info?accountId=${encodeURIComponent(accountId)}`);
  }
}

function oauthReturnParams(options = {}) {
  const params = {};
  if (options.returnTo) params.return_to = String(options.returnTo);
  if (options.returnPath) params.return_path = String(options.returnPath);
  // auth-worker's overage guard: /oauth/<platform>/start REQUIRES this for
  // EVERY connect on the Scheduler (posting) plan and every add-on overage
  // check (TASK-329 round 4 fix, guard contract 2nd review pass — not just
  // the branches that render a real price; see SocialConnectConfirm.jsx's
  // onContinue) — without it the user is 302'd back to
  // ?tab=accounts&overage=<platform> instead of started. Only ever set by a
  // caller that already showed <SocialConnectConfirm>'s confirm sheet and
  // got an explicit continue — never default it here, or an un-confirmed
  // connect would silently claim the user acknowledged a screen they never
  // saw.
  if (options.overageAck === true) params.overage_ack = '1';
  // Re-auth of an account the org already has connected (needs_reconnect /
  // expired / failed — see isReauthOfConnectedAccount, lib/socialAddon.js).
  // The auth-worker guard exempts the ack requirement when reconnect=1 AND
  // the org already has a row for that platform — a brand-new connect must
  // never send this (default OFF, same convention as overageAck above).
  if (options.reconnect === true) params.reconnect = '1';
  return params;
}

function addQuery(path, params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim()) query.set(key, String(value));
  });
  const serialized = query.toString();
  return serialized ? `${path}?${serialized}` : path;
}

/* Autopilot Client
   Per-user automated weekly cycle: Plan → Compose → Publish → Review →
   Tune → Rest. dathent-autopilot-worker owns the state machine + cron
   trigger; these HTTP methods are only for the UI to read state and
   toggle ON/OFF. */
class AutopilotClient {
  constructor(client) {
    this.client = client;
  }

  async getState({ brandProfileId, fresh = false } = {}) {
    // Short cache — Dashboard polls but doesn't need second-level freshness
    // (cron runs every 15 min anyway).
    const query = brandProfileId === undefined ? this.client._brandQuery()
      : brandProfileId ? `?brandProfileId=${encodeURIComponent(brandProfileId)}` : '';
    return this.client.get(`/api/autopilot/state${query}`, fresh ? { dedupe: false } : { cacheTTL: 15 * 1000 });
  }

  async setState({ enabled, timezone, cadence, brandProfileId } = {}) {
    return this.client.put('/api/autopilot/state', this.client._withBrand({ enabled, timezone, cadence,
      ...(brandProfileId !== undefined ? { brandProfileId } : {}),
    }), {
      invalidate: ['/api/autopilot/state', '/api/autopilot/runs'],
    });
  }

  async listRuns({ limit = 10 } = {}) {
    return this.client.get(`/api/autopilot/runs?limit=${limit}${this.client._brandQuery('&')}`, { cacheTTL: 5 * 1000 });
  }
}

// Notifications Client
class NotificationsClient {
  constructor(client) {
    this.client = client;
  }

  async list(filters = {}) {
    const query = new URLSearchParams(filters).toString();
    return this.client.get(`/notifications?${query}`);
  }

  async markRead(id) {
    return this.client.post(`/notifications/${id}/read`);
  }

  async markAllRead() {
    return this.client.post('/notifications/read-all');
  }

  async createRecommendation(recommendation) {
    return this.client.post('/notifications/recommendations', { recommendation });
  }

  async runSmartRecommendations() {
    return this.client.post('/notifications/smart-recommendations/run');
  }

  async dismissRecommendation(recommendationId) {
    return this.client.post(`/notifications/recommendations/${encodeURIComponent(recommendationId)}/dismiss`);
  }

  async getUnreadCount() {
    return this.client.get('/notifications/unread-count');
  }

  async getPreferences() {
    return this.client.get('/notifications/preferences');
  }

  async updatePreferences(preferences) {
    return this.client.put('/notifications/preferences', preferences);
  }
}

// Billing Client
// TASK-236 (#354, EPIC-023 pricing v2) — the level x operation price list
// behind GenerationLevelPicker (components/GenerationLevelPicker.jsx).
// Proxies through the gateway (routes/credit-prices.ts) to ai-router's
// GET /internal/credits/prices (TASK-175, #294). The gateway already caches
// this in-memory for 60s per isolate; the client-side cacheTTL here is a
// second, cheaper layer so mounting the picker on several forms in one
// session doesn't refetch on every mount.
class CreditsClient {
  constructor(client) {
    this.client = client;
  }

  async getPrices({ entity, currency } = {}) {
    const params = {};
    if (entity) params.entity = entity;
    if (currency) params.currency = currency;
    const query = new URLSearchParams(params).toString();
    return this.client.get(`/api/credits/prices${query ? `?${query}` : ''}`, { cacheTTL: 60 * 1000 });
  }
}

class BillingClient {
  constructor(client) {
    this.client = client;
  }

  async getEntitlements({ fresh = false } = {}) {
    // Short TTL so the usage meters reflect a spend almost immediately. Mutating
    // calls also clear the GET cache, so a generate/regenerate updates it live.
    // TASK-331 (SPA follow-up, staging bug report 2026-09-08): `fresh` busts
    // BOTH this client's own cache (cacheTTL:0) AND the api-gateway's own
    // ~60s per-org entitlements memo via the `?fresh=1` querystring the
    // gateway now special-cases — used ONLY by
    // refetchEntitlementsUntilPlanMatches's post-switch verification loop.
    // Never pass this from ordinary dashboard polling: every fresh=1 read
    // defeats the gateway's memo for the whole org, not just this tab.
    return this.client.get(`/api/billing/entitlements${fresh ? '?fresh=1' : ''}`, {
      cacheTTL: fresh ? 0 : 15 * 1000,
      // A fresh read follows a mutation and must not attach to a fresh request
      // that began before that mutation completed.
      dedupe: !fresh,
    });
  }

  async getFreePublishingUsage(at) {
    return this.client.get(`/api/free-publishing/usage?at=${encodeURIComponent(at)}`, { cacheTTL: 0, dedupe: false });
  }

  async getPlans() {
    return this.client.get('/api/billing/plans');
  }

  async getPostingQuote(add = 1) {
    return this.client.get(`/api/billing/posting/quote?add=${encodeURIComponent(add)}`);
  }

  async getScheduledPostingQuote() {
    // This quote is scoped by the active organization in auth. Never dedupe
    // across an account switch: an older in-flight GET has the same URL but
    // belongs to a different organization.
    return this.client.get('/api/billing/posting/quote?add=0&scheduled=1', { cacheTTL: 0, dedupe: false });
  }

  // TASK-329 slice C: mirrors getPostingQuote above — the Scheduler add-on
  // preview for a plan whose social_accounts_total is about to be (or
  // already is) exceeded. Gateway proxy added in slice B
  // (routes/billing.ts `/api/billing/social-addon/quote`); response shape
  // documented in stripe-worker's handlers/social-addon-quote.ts.
  async getSocialAddonQuote(add = 1) {
    return this.client.get(`/api/billing/social-addon/quote?add=${encodeURIComponent(add)}`);
  }

  async getInvoices() {
    return this.client.get('/api/billing/invoices');
  }

  // Saved card on the current subscription for Settings → Payment method.
  // Returns { card, hasSubscription }: card is brand/last4/expiry (null for a
  // wallet/Link PM with no card details); hasSubscription includes payment
  // recovery and a scheduled cancellation while the subscription still exists.
  async getPaymentMethod({ fresh = false } = {}) {
    return this.client.get('/api/billing/payment-method', fresh ? { dedupe: false } : { cacheTTL: 30 * 1000 });
  }

  async createCheckoutSession({ planKey, successUrl = null, cancelUrl = null, trialSignup }) {
    return this.client.post('/api/billing/create-checkout', {
      planKey,
      successUrl,
      cancelUrl,
      ...(trialSignup === undefined ? {} : { trialSignup }),
    });
  }

  // Model B card-capture (Payment Element). Step 1: resolve the card step state
  // (active / card_on_file / collect). Pass forceCollect to always get a fresh
  // form (the "use a different card" escape hatch).
  async createTrialSetupIntent({ forceCollect } = {}) {
    return this.client.post('/api/billing/trial/setup-intent', { forceCollect });
  }

  // Step 2: create the trialing subscription with a saved card. Pass either the
  // just-confirmed SetupIntent id, or a payment-method id already on file.
  // `planKey` (onboarding tier-picker) is the chosen self-service plan — omitted
  // (undefined → dropped by JSON.stringify) pre-flip / by a stale client, in
  // which case the backend resolves the default plan server-side (unchanged).
  async activateTrial({ setupIntentId, paymentMethodId, planKey, taxQuote } = {}) {
    return this.client.post('/api/billing/trial/activate', { setupIntentId, paymentMethodId, planKey, taxQuote });
  }

  async previewTrialTax({ planKey, nativeLocation, paymentMethodId } = {}) {
    return this.client.post('/api/billing/trial/tax-preview', { planKey, nativeLocation, paymentMethodId });
  }

  async activateTrialNow() {
    return this.client.post('/api/billing/trial/activate-now', undefined, {
      invalidate: '/api/billing/entitlements',
    });
  }

  // Update the saved card from Settings. Step 1: a SetupIntent for a fresh card.
  async createUpdateCardSetupIntent() {
    return this.client.post('/api/billing/payment-method/setup-intent', {});
  }

  // Finalize a hosted setup session (Settings), or a legacy SetupIntent.
  // The server verifies ownership and updates customer/subscription defaults.
  async updatePaymentMethod({ setupIntentId, checkoutSessionId } = {}) {
    return this.client.post('/api/billing/payment-method/update', { setupIntentId, checkoutSessionId });
  }

  async changePlan({ newPlanKey }) {
    // TASK-327: without this, post()'s auto-invalidation (see below) only
    // clears the POST's OWN path (/api/billing/change-plan, never itself a
    // cached GET) — the entitlements GET cache survived a plan change for up
    // to its own TTL. Matches activateTrialNow()'s explicit override.
    return this.client.post('/api/billing/change-plan', { newPlanKey }, {
      invalidate: '/api/billing/entitlements',
    });
  }

  async cancelSubscription({ cancelAtPeriodEnd = true, reason = null }) {
    return this.client.post('/api/billing/cancel-subscription', { cancelAtPeriodEnd, reason });
  }

  // Un-cancel a subscription scheduled to cancel at period end (Stripe
  // cancel_at_period_end → false). Positive, reversible action — no
  // confirmation needed on the caller side. Returns
  // { message, cancelAtPeriodEnd: false, currentPeriodEnd }.
  async resumeSubscription() {
    return this.client.post('/api/billing/resume-subscription', {}, {
      invalidate: '/api/billing/entitlements',
    });
  }

  // TASK-331 phase 2 (Subscription Schedules) — undo a deferred downgrade
  // (D4: "undo until then"). Positive, reversible action, same shape as
  // resumeSubscription() above — no confirmation needed on the caller side.
  // Gateway → stripe-worker releases the live Subscription Schedule and
  // clears the 4 D1 mirror columns; no-op (not an error) when nothing is
  // scheduled. Returns { message, released, scheduledPlan: null,
  // scheduledPlanEffectiveAt: null } (no-op) or { message, released: true,
  // subscriptionId, previousScheduledPlan, previousScheduledPlanEffectiveAt }.
  async cancelScheduledDowngrade() {
    return this.client.post('/api/billing/downgrade/cancel', {});
  }

  async getUpcomingInvoice({ newPlanKey }) {
    return this.client.get(`/api/billing/upcoming-invoice?newPlanKey=${encodeURIComponent(newPlanKey)}`);
  }

  async requestPlan({ planKey, message = null }) {
    return this.client.post('/api/billing/request-plan', { planKey, message });
  }

  // Hosted billing management, or a focused card update selected server-side.
  async createPortalSession({ returnUrl = null, flow } = {}) {
    return this.client.post('/api/billing/portal', { returnUrl, ...(flow ? { flow } : {}) });
  }
}

// API Keys Client
class ApiKeysClient {
  constructor(client) {
    this.client = client;
  }

  async list() {
    return this.client.get('/api/auth/api-keys');
  }

  async create({ name, scopes, expiresAt = null }) {
    return this.client.post('/api/auth/api-keys', { name, scopes, expiresAt });
  }

  async revoke(id) {
    return this.client.delete(`/api/auth/api-keys/${id}`);
  }

  async listGrants() {
    return this.client.get('/api/connectors/grants');
  }

  async connectorsStatus() {
    return this.client.get('/api/connectors/status');
  }

  async revokeGrant(id) {
    return this.client.delete(`/api/connectors/grants/${encodeURIComponent(id)}`);
  }
}

// Platforms Client
class PlatformsClient {
  constructor(client) {
    this.client = client;
  }

  async registry() {
    return this.client.get('/api/platforms/registry', { cacheTTL: 5 * 60 * 1000 });
  }

  async get(platform) {
    return this.client.get(`/api/platforms/${encodeURIComponent(platform)}`, { cacheTTL: 5 * 60 * 1000 });
  }
}

// Carousel Client
class CarouselClient {
  constructor(client) {
    this.client = client;
  }

  async generate({ contentItemId, contentVersionId = null, platforms, slideCount }) {
    return this.client.post('/api/carousel/generate', {
      contentItemId,
      contentVersionId,
      platforms,
      slideCount,
    });
  }

  async listSlides(versionId) {
    return this.client.get(`/api/carousel/versions/${encodeURIComponent(versionId)}/slides`);
  }
}

// Assistant Client
class AssistantClient {
  constructor(client) {
    this.client = client;
  }

  async chat({ message, currentScreen, selectedEntity = null, actionMetadata = null, responseLanguage = null }) {
    // Send the user's chosen UI language so the assistant always replies in it
    // and never language-flips on a short message. Backend normalizes the code
    // and falls back to content detection if it's missing/unsupported.
    const lang = responseLanguage || (i18n && i18n.language) || null;
    return this.client.post('/api/assistant/chat', {
      message,
      currentScreen,
      selectedEntity,
      actionMetadata,
      ...(lang ? { responseLanguage: lang } : {}),
    });
  }

  async messages({ limit = 50 } = {}) {
    return this.client.get(`/api/assistant/messages?limit=${encodeURIComponent(limit)}`);
  }

  async confirmAction({ action, confirmed = true }) {
    return this.client.post('/api/assistant/actions/confirm', { action, confirmed });
  }

  async previewAction({ action }) {
    return this.client.post('/api/assistant/actions/preview', { action, confirmed: false });
  }

  async uploadAttachment(attachment) {
    return this.client.post('/api/assistant/attachments', attachment);
  }
}

// Research Client — marketing research reports (research-worker via gateway).
class ResearchClient {
  constructor(client) {
    this.client = client;
  }

  // Optional explicit brandProfileId (e.g. the add-brand chain tracking a
  // specific new brand regardless of which brand is active); defaults to the
  // active brand's scope.
  async list(params = {}) {
    const q = params.brandProfileId
      ? `?brandProfileId=${encodeURIComponent(params.brandProfileId)}`
      : this.client._brandQuery();
    return this.client.get(`/api/research/reports${q}`);
  }

  async get(id) {
    return this.client.get(`/api/research/reports/${encodeURIComponent(id)}`);
  }

  async create(data = {}) {
    return this.client.post('/api/research/reports', this.client._withBrand(data), {
      invalidate: ['/api/research'],
    });
  }

  async refresh(id) {
    return this.client.post(`/api/research/reports/${encodeURIComponent(id)}/refresh`, undefined, {
      invalidate: ['/api/research'],
    });
  }

  async cancel(id) {
    return this.client.post(`/api/research/reports/${encodeURIComponent(id)}/cancel`, undefined, {
      invalidate: ['/api/research'],
    });
  }

  async active() {
    return this.client.get('/api/research/active');
  }
}

// Brand Kit asset library — tagged reference assets (visual-worker via gateway,
// /api/visual/brand-assets/*). Cache is invalidated on any mutation so the
// library grid + reference pickers reflect changes immediately.
class BrandAssetsClient {
  constructor(client) {
    this.client = client;
  }

  // includeVideos opts the caller into rendered reels (media_assets type=video)
  // alongside the image library. OFF by default so the reference pickers and
  // research consumers stay image-only; only the Media gallery passes it.
  //
  // TASK-325 fix (blocker #1): `origin` is a SEPARATE axis from `tag` — the
  // "From posts" auto-save history vs. the 5 native Brand Kit tags. Omitted
  // (undefined) keeps every existing caller byte-identical: native rows only,
  // so a post-provenance flood can never evict a native asset (the primary
  // logo included) from a picker that never asked for post rows. Pass
  // origin: 'post' for the "From posts" filter chip, or origin: 'all' for a
  // combined gallery (Brand Kit "All" tab, New Post my-media picker).
  async list(tag, { includeVideos = false, origin, brandProfileId } = {}) {
    const params = new URLSearchParams();
    if (tag) params.set('tag', tag);
    if (includeVideos) params.set('includeVideos', '1');
    if (origin) params.set('origin', origin);
    if (brandProfileId) params.set('brandProfileId', brandProfileId);
    const q = params.toString();
    return this.client.get(`/api/visual/brand-assets${q ? `?${q}` : ''}${brandProfileId ? '' : this.client._brandQuery(q ? '&' : '?')}`, { cacheTTL: 0 });
  }

  /* TASK-053: onProgress switches this to the XHR path so the caller can show
     real byte progress. Without it the behaviour is byte-identical to before
     (plain fetch + cache invalidation), so existing call sites are untouched. */
  async upload({ imageBase64, dataUrl, fileName, tag, brandProfileId, onProgress, signal } = {}) {
    const body = this.client._withBrand({ imageBase64, dataUrl, fileName, tag, ...(brandProfileId ? { brandProfileId } : {}) });
    if (typeof onProgress !== 'function' && !signal) {
      return this.client.post('/api/visual/brand-assets', body, {
        invalidate: ['/api/visual/brand-assets'],
      });
    }
    const result = await this.client.uploadWithProgress('/api/visual/brand-assets', JSON.stringify(body), {
      headers: { 'Content-Type': 'application/json' },
      onProgress,
      signal
    });
    this.client.invalidateCache('/api/visual/brand-assets');
    return result;
  }

  async generate({ prompt, tag, width, height, referenceAssetIds } = {}) {
    return this.client.post('/api/visual/brand-assets/generate', this.client._withBrand({ prompt, tag, width, height, referenceAssetIds }), {
      invalidate: ['/api/visual/brand-assets'],
    });
  }

  // Reel (9:16 narrated slideshow) SUBMIT — durable 202, returns { operationId }.
  // v1 backend (visual-worker submitVideoReel) stitches the picked image frames
  // (frameAssetIds, 1–8) with optional narration (bool) + script. Admin-gated +
  // flag-dark server side; the completed reel surfaces in the library via the
  // VIDEO_RENDER op.
  // hookLine: optional user override for the bold first-3-seconds line (≤9
  // words); empty = the AI writes the hook server-side.
  // prompt/brandVoice: the backend also accepts a describe→video (V-PROMPT)
  // mode — pass `prompt` instead of frameAssetIds to generate frames from a
  // text description; brandVoice (default true) folds in brand_profiles + the
  // active strategy for the scene plan, or false for a brand-free video.
  // language: raw locale code (e.g. "uk"), NOT a display name — drives
  // narration/subtitles language server-side.
  // referenceAssetIds: describe-mode ONLY ("1 click with a reference") — owned
  // image asset ids (≤3) the backend conditions every generated scene frame on
  // via runImagePrompt's explicit-composition channel. Additive/optional —
  // absent (undefined) is byte-identical to before this param existed; the
  // backend ignores it entirely when frameAssetIds is also present (photos mode).
  async generateVideo({ prompt, frameAssetIds, narration = true, script, hookLine, brandVoice = true, language, referenceAssetIds, quality } = {}) {
    return this.client.post('/api/visual/videos/generate', this.client._withBrand({ prompt, frameAssetIds, narration, script, hookLine, brandVoice, language, referenceAssetIds, quality }), {
      invalidate: ['/api/visual/brand-assets'],
    });
  }

  // Save an already-owned post image (by its media_assets id) into the library.
  // The bytes are copied server-side (the post image URL has no CORS), so the
  // client only sends the id + optional tag.
  async saveFromMedia({ mediaAssetId, tag } = {}) {
    return this.client.post('/api/visual/brand-assets/from-media', { mediaAssetId, tag }, {
      invalidate: ['/api/visual/brand-assets'],
    });
  }

  async updateTag(id, tag) {
    return this.client.patch(`/api/visual/brand-assets/${encodeURIComponent(id)}`, { tag }, {
      invalidate: ['/api/visual/brand-assets'],
    });
  }

  async setPrimaryLogo(id) {
    return this.client.post(`/api/visual/brand-assets/${encodeURIComponent(id)}/primary-logo`, undefined, {
      invalidate: ['/api/visual/brand-assets'],
    });
  }

  async remove(id) {
    return this.client.delete(`/api/visual/brand-assets/${encodeURIComponent(id)}`, {
      invalidate: ['/api/visual/brand-assets'],
    });
  }
}

// "Upload your own video" — RAW-body upload (mirrors ReferralClient.uploadPhoto's
// pattern above: fetch() directly, body: file, Content-Type from the file
// itself), since request()/post() always JSON.stringify their body and a video
// file can't go through that path. Not routed through BrandAssetsClient because
// the endpoint lives at /api/visual/videos/*, not /api/visual/brand-assets — but
// the returned asset shares the same shape (id, url, posterUrl, durationSeconds,
// mimeType, type:'video', provider), so it drops straight into the existing
// brand-assets list/grid.
class VideosClient {
  constructor(client) {
    this.client = client;
  }

  // onProgress is accepted but currently a no-op — plain fetch() has no upload-
  // progress plumbing (that needs XHR), and this client has never carried an XHR
  // path. Callers show an indeterminate spinner instead (see BrandKitScreen).
  async upload(file, { fileName, width, height, durationSeconds, brandProfileId, onProgress } = {}) {
    if (!(file instanceof Blob)) {
      throw new DathentAPIError('upload requires a File or Blob', 'VALIDATION_ERROR', 400);
    }
    const params = new URLSearchParams();
    if (fileName) params.set('fileName', fileName);
    if (Number.isFinite(width)) params.set('width', String(Math.round(width)));
    if (Number.isFinite(height)) params.set('height', String(Math.round(height)));
    if (Number.isFinite(durationSeconds)) params.set('durationSeconds', String(Math.round(durationSeconds)));
    if (brandProfileId) params.set('brandProfileId', brandProfileId);
    const qs = params.toString();
    const url = `${this.client.baseURL}/api/visual/videos/upload${qs ? `?${qs}` : ''}${brandProfileId ? '' : this.client._brandQuery(qs ? '&' : '?')}`;
    const headers = { 'Content-Type': file.type || 'video/mp4' };
    const token = this.client.getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    // TASK-053: with onProgress, go through XHR for real byte progress —
    // this is the big upload in the product, the one that most needs a bar.
    if (typeof onProgress === 'function') {
      const result = await this.client.uploadWithProgress(url, file, {
        headers: { 'Content-Type': file.type || 'video/mp4' },
        onProgress
      });
      this.client.invalidateCache('/api/visual/brand-assets');
      return result;
    }
    let response;
    try {
      response = await fetch(url, { method: 'POST', headers, body: file, credentials: 'include', cache: 'no-store' });
    } catch (error) {
      throw new DathentAPIError(error?.message || 'Network error', 'NETWORK_ERROR', 0, { originalError: error });
    }
    const contentType = response.headers.get('content-type');
    const json = contentType && contentType.includes('application/json')
      ? await response.json().catch(() => ({}))
      : { message: await response.text().catch(() => '') };
    if (!response.ok) {
      const apiError = json?.error || json || {};
      const message = typeof apiError === 'string' ? json?.message || apiError : apiError.message || json?.message || 'Video upload failed';
      const code = typeof apiError === 'string' ? apiError : apiError.code || json?.code || 'UNKNOWN_ERROR';
      const details = typeof apiError === 'string' ? json?.details || null : apiError.details || json?.details || null;
      throw new DathentAPIError(message, code, response.status, details);
    }
    this.client.invalidateCache('/api/visual/brand-assets');
    return json?.ok === true && Object.prototype.hasOwnProperty.call(json, 'data') ? json.data : json;
  }

  // Poster capture (client-drawn JPEG dataURL, ≤400KB decoded) — separate call
  // since the upload endpoint's body IS the raw video, no room for a second field.
  async setPoster(id, imageBase64) {
    return this.client.post(`/api/visual/videos/${encodeURIComponent(id)}/poster`, { imageBase64 }, {
      invalidate: ['/api/visual/brand-assets'],
    });
  }

  // TASK-334: DELETE for a caller's own uploaded video. Uploaded videos never
  // get a brand_reference_assets row (see visual-worker's deleteUploadedVideo),
  // so routing this through BrandAssetsClient.remove() (which hits
  // /api/visual/brand-assets/:id → deleteBrandAsset, brand_reference_assets-only)
  // 404s every time — this hits the dedicated /api/visual/videos/:id →
  // deleteUploadedVideo path instead, which also releases the poster's R2 bytes.
  // Same invalidate target as upload/setPoster above: the Media grid and every
  // reference picker key off the shared /api/visual/brand-assets list cache.
  async remove(id) {
    return this.client.delete(`/api/visual/videos/${encodeURIComponent(id)}`, {
      invalidate: ['/api/visual/brand-assets'],
    });
  }
}

// TASK-334: pure dispatch helper for the Media library delete button
// (BrandKitScreen's confirmRemove) — an asset's mimeType is the only signal
// the client has for which backend row it lives in (brand_reference_assets
// for images/logos, media_assets type='video' provider='upload' for uploaded
// videos), so route on that same `startsWith('video/')` check the rest of
// BrandKitScreen already uses (isVideoAsset). Kept standalone (not a method
// on either client) so it's trivially testable with two spy objects instead
// of a full DathentClient + fetch mock.
export function deleteBrandKitAsset(apiClient, asset) {
  const isVideo = Boolean(asset) && (asset.mimeType || '').startsWith('video/');
  return isVideo ? apiClient.videos.remove(asset.id) : apiClient.brandAssets.remove(asset.id);
}

// Analytics Client
class AnalyticsClient {
  constructor(client) {
    this.client = client;
  }

  async getInsights({ windowDays = 30 } = {}) {
    return this.client.get(`/api/analytics/insights?window=${encodeURIComponent(windowDays)}`);
  }

  async getPostsSummary({ days = 30 } = {}) {
    return this.client.get(`/api/analytics/posts-summary?days=${encodeURIComponent(days)}`, { cacheTTL: 2 * 60 * 1000 });
  }

  // Consolidated multi-metric series for the dashboard AnalyticsPanel
  // (engagement / reach / platforms / interactions). Short cache — it's
  // re-fetched when the dashboard mounts.
  async overview({ days = 90 } = {}) {
    return this.client.get(`/api/analytics/overview?days=${encodeURIComponent(days)}`, { cacheTTL: 2 * 60 * 1000 });
  }

  async createRecommendationNotifications({ windowDays = 30, recommendationIds = [] } = {}) {
    return this.client.post('/api/analytics/recommendations/notify', { windowDays, recommendationIds });
  }

  async getBestTimes({ platform, count = 7, windowDays, horizonDays } = {}) {
    const params = new URLSearchParams();
    if (platform) params.set('platform', String(platform).toLowerCase());
    if (count) params.set('count', String(count));
    if (windowDays) params.set('windowDays', String(windowDays));
    if (horizonDays) params.set('horizonDays', String(horizonDays));
    const qs = params.toString();
    return this.client.get(`/api/analytics/best-times${qs ? `?${qs}` : ''}`, { cacheTTL: 60 * 60 * 1000 });
  }
}

// Threads-specific actions for the App Review demo + product features:
// post-level insights, replies feed, reply send, hide/unhide reply, delete.
// All routes scope to the authenticated user via the x-dathent-user-id
// header set by the API gateway.
class ThreadsClient {
  constructor(client) {
    this.client = client;
  }

  async getInsights(postId) {
    // Cached briefly — reviewers refresh insights button shouldn't hammer
    // Threads' rate-limited insights endpoint.
    return this.client.get(`/api/threads/posts/${encodeURIComponent(postId)}/insights`, { cacheTTL: 30 * 1000 });
  }

  async listReplies(postId) {
    return this.client.get(`/api/threads/posts/${encodeURIComponent(postId)}/replies`, { cacheTTL: 15 * 1000 });
  }

  async sendReply(postId, text) {
    return this.client.post(`/api/threads/posts/${encodeURIComponent(postId)}/reply`, { text }, {
      invalidate: [`/api/threads/posts/${encodeURIComponent(postId)}/replies`],
    });
  }

  async hideReply(replyId) {
    return this.client.post(`/api/threads/replies/${encodeURIComponent(replyId)}/hide`, null);
  }

  async unhideReply(replyId) {
    return this.client.post(`/api/threads/replies/${encodeURIComponent(replyId)}/unhide`, null);
  }

  async deletePost(postId) {
    return this.client.delete(`/api/threads/posts/${encodeURIComponent(postId)}`);
  }
}

// Instagram Business client — mirrors Threads. Uses Meta Graph API for IG
// Business / Creator accounts. All routes scope to the authenticated user
// via the x-dathent-user-id header set by the API gateway.
class InstagramClient {
  constructor(client) {
    this.client = client;
  }

  async getInsights(postId) {
    return this.client.get(`/api/instagram/posts/${encodeURIComponent(postId)}/insights`, { cacheTTL: 30 * 1000 });
  }

  async listComments(postId) {
    return this.client.get(`/api/instagram/posts/${encodeURIComponent(postId)}/comments`, { cacheTTL: 15 * 1000 });
  }

  async sendComment(postId, text) {
    return this.client.post(`/api/instagram/posts/${encodeURIComponent(postId)}/comment`, { text }, {
      invalidate: [`/api/instagram/posts/${encodeURIComponent(postId)}/comments`],
    });
  }

  async replyToComment(commentId, text) {
    return this.client.post(`/api/instagram/comments/${encodeURIComponent(commentId)}/reply`, { text });
  }

  async hideComment(commentId) {
    return this.client.post(`/api/instagram/comments/${encodeURIComponent(commentId)}/hide`, null);
  }

  async unhideComment(commentId) {
    return this.client.post(`/api/instagram/comments/${encodeURIComponent(commentId)}/unhide`, null);
  }

  async deletePost(postId) {
    return this.client.delete(`/api/instagram/posts/${encodeURIComponent(postId)}`);
  }

  // Direct Messages (instagram_business_manage_messages)
  async listConversations(accountId) {
    const qs = accountId ? `?accountId=${encodeURIComponent(accountId)}` : '';
    return this.client.get(`/api/instagram/conversations${qs}`, { cacheTTL: 10 * 1000 });
  }

  async getConversation(conversationId, accountId) {
    const qs = accountId ? `?accountId=${encodeURIComponent(accountId)}` : '';
    return this.client.get(`/api/instagram/conversations/${encodeURIComponent(conversationId)}${qs}`, { cacheTTL: 5 * 1000 });
  }

  async sendMessage(conversationId, text, accountId) {
    const qs = accountId ? `?accountId=${encodeURIComponent(accountId)}` : '';
    return this.client.post(`/api/instagram/conversations/${encodeURIComponent(conversationId)}/messages${qs}`, { text }, {
      invalidate: [
        `/api/instagram/conversations/${encodeURIComponent(conversationId)}`,
        `/api/instagram/conversations`,
      ],
    });
  }
}

// Referral Client — referrer profile + lead-capture signup + payouts
class ReferralClient {
  constructor(client) {
    this.client = client;
  }

  async lookupByCode(code) {
    return this.client.get(`/api/referral/r/${encodeURIComponent(code)}`, { cacheTTL: 30 * 1000 });
  }

  async submitSignup({ code, email, contactKind, contactValue, locale, utm }) {
    return this.client.post('/api/referral/signup', {
      code,
      email,
      contactKind,
      contactValue,
      locale,
      utm,
    });
  }

  async getMyProfile() {
    return this.client.get('/api/referral/me');
  }

  async upsertMyProfile({ code, regenerateCode, displayName, studio, photoUrl, photoPosition, avatarGradient, customMessage } = {}) {
    return this.client.post('/api/referral/me', {
      code,
      regenerateCode,
      displayName,
      studio,
      photoUrl,
      photoPosition,
      avatarGradient,
      customMessage,
    }, { invalidate: ['/api/referral/me'] });
  }

  async uploadPhoto(file) {
    if (!(file instanceof Blob)) {
      throw new DathentAPIError('uploadPhoto requires a File or Blob', 'VALIDATION_ERROR', 400);
    }
    const contentType = file.type || 'application/octet-stream';
    const url = `${this.client.baseURL}/api/referral/photo`;
    const headers = { 'Content-Type': contentType };
    const token = this.client.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: file,
      credentials: 'include',
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
      const apiError = json?.error || {};
      throw new DathentAPIError(
        apiError.message || json?.message || 'Photo upload failed',
        apiError.code || 'UPLOAD_FAILED',
        response.status,
        apiError.details || null,
      );
    }
    this.client.invalidateCache('/api/referral/me');
    return json?.ok === true && Object.prototype.hasOwnProperty.call(json, 'data') ? json.data : json;
  }

  async listSignups() {
    return this.client.get('/api/referral/me/signups');
  }

  async listPayouts() {
    return this.client.get('/api/referral/me/payouts');
  }

  async requestPayout({ amountCents, method, methodDetails, notes } = {}) {
    return this.client.post('/api/referral/me/payouts/request', {
      amountCents,
      method,
      methodDetails,
      notes,
    }, { invalidate: ['/api/referral/me/payouts'] });
  }

  // Referral v2 — partner program application (S1 -> S2). Server gates on an
  // active subscription + accepted rules; granted tier is decided at approval.
  async apply({ acceptedRules, desiredTier, legalEntityType, contactValue } = {}) {
    return this.client.post('/api/referral/apply', {
      acceptedRules,
      desiredTier,
      legalEntityType,
      contactValue,
    }, { invalidate: ['/api/referral/me'] });
  }
}

// Admin Pre-registrations Client — read endpoints over `pre_registrations`.
// Gated by `admins` table server-side; the UI hides entry points based on
// `user.isAdmin`, but the API enforces independently.
class AdminPreregsClient {
  constructor(client) {
    this.client = client;
  }

  async list({ status = 'all', q = '', limit = 50, offset = 0 } = {}) {
    const params = new URLSearchParams();
    if (status && status !== 'all') params.set('status', status);
    if (q) params.set('q', q);
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    return this.client.get(`/api/admin/preregs?${params.toString()}`);
  }

  async stats() {
    return this.client.get('/api/admin/preregs/stats');
  }

  // Returns the absolute URL for the CSV export. The browser handles the
  // download natively via `<a download>` — calling this through fetch() would
  // discard the Content-Disposition filename.
  exportCsvUrl({ status = 'all', q = '' } = {}) {
    const params = new URLSearchParams();
    if (status && status !== 'all') params.set('status', status);
    if (q) params.set('q', q);
    const qs = params.toString();
    return this.client.url(`/api/admin/preregs/export.csv${qs ? `?${qs}` : ''}`);
  }
}

// Admin Referral Client — operator review (gated by email allowlist server-side)
class AdminReferralClient {
  constructor(client) {
    this.client = client;
  }

  async stats() {
    return this.client.get('/api/admin/referrals/stats');
  }

  async listSignups({ status = 'all', limit = 500 } = {}) {
    const q = new URLSearchParams();
    if (status && status !== 'all') q.set('status', status);
    q.set('limit', String(limit));
    return this.client.get(`/api/admin/referrals/signups?${q.toString()}`);
  }

  async listPayouts({ status = 'all', limit = 200 } = {}) {
    const q = new URLSearchParams();
    if (status && status !== 'all') q.set('status', status);
    q.set('limit', String(limit));
    return this.client.get(`/api/admin/referrals/payouts?${q.toString()}`);
  }

  async markPayoutPaid(id, { method, methodDetails, notes } = {}) {
    return this.client.post(
      `/api/admin/referrals/payouts/${encodeURIComponent(id)}/mark-paid`,
      { method, methodDetails, notes },
      { invalidate: ['/api/admin/referrals/payouts'] }
    );
  }

  // Referral v2 — application moderation queue + partner registry.
  async listApplications({ scope = 'pending' } = {}) {
    const q = new URLSearchParams();
    q.set('scope', scope);
    return this.client.get(`/api/admin/referrals/applications?${q.toString()}`);
  }

  async reviewApplication(id, { action, partnerType, parentReferrerId, reason } = {}) {
    return this.client.post(
      `/api/admin/referrals/applications/${encodeURIComponent(id)}/review`,
      { action, partnerType, parentReferrerId, reason },
      { invalidate: ['/api/admin/referrals/applications'] }
    );
  }

  async updateReferrer(id, { partnerType, isPaused, parentReferrerId } = {}) {
    return this.client.post(
      `/api/admin/referrals/partners/${encodeURIComponent(id)}`,
      { partnerType, isPaused, parentReferrerId },
      { invalidate: ['/api/admin/referrals/applications'] }
    );
  }

  async listCredits({ status = 'all', limit = 300 } = {}) {
    const q = new URLSearchParams();
    if (status && status !== 'all') q.set('status', status);
    q.set('limit', String(limit));
    return this.client.get(`/api/admin/referrals/credits?${q.toString()}`);
  }
}

// Admin Broadcast Client — mailing foundation (gated server-side by requireAdmin).
// Thin wrapper over /api/admin/broadcast/* which proxies to the notification
// worker. Mass send is additionally gated by a backend env flag.
// Admin "Refresh activation" — force-regenerate a target user's strategy +
// first post. Server-side admin-gated; the SPA route carries no client guard
// (security is 100% server-side via requireAdmin). getOperation polls the
// durable async-op by id (cacheTTL:0 so reload/poll reflects live state).
class AdminActivationClient {
  constructor(client) {
    this.client = client;
  }

  async refresh(email) {
    return this.client.post('/api/admin/activation/refresh', { email });
  }

  async getOperation(operationId) {
    return this.client.get(`/api/admin/activation/operations/${encodeURIComponent(operationId)}`, { cacheTTL: 0 });
  }
}

class AdminBroadcastClient {
  constructor(client) {
    this.client = client;
  }

  async listCampaigns() {
    return this.client.get('/api/admin/broadcast/campaigns');
  }

  async getCampaign(id) {
    return this.client.get(`/api/admin/broadcast/campaigns/${encodeURIComponent(id)}`);
  }

  async createCampaign(payload) {
    return this.client.post('/api/admin/broadcast/campaigns', payload);
  }

  async previewAudience(id) {
    return this.client.post(`/api/admin/broadcast/campaigns/${encodeURIComponent(id)}/audience`, {});
  }

  async sendTest(id, to) {
    return this.client.post(`/api/admin/broadcast/campaigns/${encodeURIComponent(id)}/test`, { to });
  }

  async send(id) {
    return this.client.post(`/api/admin/broadcast/campaigns/${encodeURIComponent(id)}/send`, {});
  }

  async dispatch(id) {
    return this.client.post(`/api/admin/broadcast/campaigns/${encodeURIComponent(id)}/dispatch`, {});
  }
}

// Admin Support Hub — /api/admin/support/* (gated server-side by requireAdmin,
// proxied auth-worker -> support-worker; see admin-support.ts on both sides).
// Thin wrapper, no client-side guard (mirrors AdminBroadcast/AdminActivation —
// security is 100% server-side). Every read uses cacheTTL:0 so the operator
// inbox always reflects the live D1 state (no stale conversation lists).
class AdminSupportClient {
  constructor(client) {
    this.client = client;
  }

  async list({ status, source, ticketType, limit, cursor } = {}) {
    const q = new URLSearchParams();
    if (status && status !== 'all') q.set('status', status);
    if (source && source !== 'all') q.set('source', source);
    if (ticketType && ticketType !== 'all') q.set('ticket_type', ticketType);
    if (limit) q.set('limit', String(limit));
    if (cursor) q.set('cursor', cursor);
    const qs = q.toString();
    return this.client.get(`/api/admin/support/conversations${qs ? `?${qs}` : ''}`, { cacheTTL: 0 });
  }

  async get(id) {
    return this.client.get(`/api/admin/support/conversations/${encodeURIComponent(id)}`, { cacheTTL: 0 });
  }

  // Manual ticket creation (owner-facing "New ticket" button — no visitor
  // conversation exists yet). ticketType defaults to 'bug' in the UI; name is
  // a short title, message is the description body.
  async create({ ticketType, name, message, assignee } = {}) {
    return this.client.post('/api/admin/support/conversations', {
      ticket_type: ticketType,
      name,
      message,
      assignee,
    });
  }

  async reply(id, message, author) {
    return this.client.post(`/api/admin/support/conversations/${encodeURIComponent(id)}/reply`, { message, author });
  }

  // classify accepts ticketType: string | null (null = "not a ticket"),
  // status, assignee: string | null. Only supplied keys are sent.
  async classify(id, { ticketType, status, assignee } = {}) {
    const body = {};
    if (ticketType !== undefined) body.ticket_type = ticketType;
    if (status !== undefined) body.status = status;
    if (assignee !== undefined) body.assignee = assignee;
    return this.client.post(`/api/admin/support/conversations/${encodeURIComponent(id)}/classify`, body);
  }

  async bridgeStatus() {
    return this.client.get('/api/admin/support/bridge-status', { cacheTTL: 0 });
  }
}

// Admin-managed plans — /api/admin/plans/* (gated server-side by requireAdmin,
// proxied auth-worker -> stripe-worker; see admin-plans.ts on both sides).
// Thin wrapper, no client-side guard (mirrors AdminBroadcast/AdminActivation —
// security is 100% server-side). Every write requires changeReason; updates
// additionally require expectedVersion for optimistic-concurrency (409 on
// conflict — the screen re-fetches the list on conflict rather than retrying).
class AdminPlansClient {
  constructor(client) {
    this.client = client;
  }

  async list() {
    return this.client.get('/api/admin/plans', { cacheTTL: 0 });
  }

  async update(planKey, payload) {
    return this.client.put(`/api/admin/plans/${encodeURIComponent(planKey)}`, payload, {
      invalidate: ['/api/admin/plans'],
    });
  }

  async updatePrice(planKey, payload) {
    return this.client.put(`/api/admin/plans/${encodeURIComponent(planKey)}/prices`, payload, {
      invalidate: ['/api/admin/plans'],
    });
  }

  async rollback(planKey, payload) {
    return this.client.post(`/api/admin/plans/${encodeURIComponent(planKey)}/rollback`, payload, {
      invalidate: ['/api/admin/plans'],
    });
  }
}

// Admin Users — /api/admin/users/* (gated server-side by requireAdmin; see
// admin-users.ts [list + stats] and admin-user-detail.ts [detail] on the
// auth-worker side, TASK-108/109/EPIC-017). Thin wrapper, no client-side
// guard (mirrors AdminActivation/AdminPlans/AdminSupport — security is 100%
// server-side). Every read uses cacheTTL:0 so the list/stats/detail always
// reflect the live D1 state. `sort` is validated server-side against a
// whitelist (400 on an unknown value) — this client passes it through as-is,
// it never invents a mapping.
class AdminUsersClient {
  constructor(client) {
    this.client = client;
  }

  async list({
    status, q, plan, subscription, trial,
    createdFrom, createdTo, periodEndFrom, periodEndTo,
    source, sort, order, limit, offset,
  } = {}) {
    const qs = new URLSearchParams();
    if (status && status !== 'all') qs.set('status', status);
    if (q) qs.set('q', q);
    if (plan) qs.set('plan', plan);
    if (subscription && subscription !== 'all') qs.set('subscription', subscription);
    if (trial === '1' || trial === '0') qs.set('trial', trial);
    if (createdFrom) qs.set('createdFrom', createdFrom);
    if (createdTo) qs.set('createdTo', createdTo);
    if (periodEndFrom) qs.set('periodEndFrom', periodEndFrom);
    if (periodEndTo) qs.set('periodEndTo', periodEndTo);
    if (source) qs.set('source', source);
    if (sort) qs.set('sort', sort);
    if (order) qs.set('order', order);
    if (limit) qs.set('limit', String(limit));
    if (offset) qs.set('offset', String(offset));
    const qsStr = qs.toString();
    return this.client.get(`/api/admin/users${qsStr ? `?${qsStr}` : ''}`, { cacheTTL: 0 });
  }

  async stats() {
    return this.client.get('/api/admin/users/stats', { cacheTTL: 0 });
  }

  async funnel(days = 30) {
    return this.client.get(`/api/admin/funnel?days=${encodeURIComponent(days)}`, { cacheTTL: 0 });
  }

  async detail(id) {
    return this.client.get(`/api/admin/users/${encodeURIComponent(id)}/detail`, { cacheTTL: 0 });
  }
}

// TASK-167 / issue #273 — admin behavior/funnel dashboard. Reads
// dathent-api-gateway-worker's own GET /api/admin/behavior/{funnel,events}
// (own-D1 aggregation over funnel_events — see that worker's
// src/routes/admin-behavior.ts), NOT proxied through auth-worker like
// AdminUsersClient above. cacheTTL:0 on every call: this is an ops tool, the
// admin always wants the live D1 state, same convention as AdminUsersClient.
class AdminBehaviorClient {
  constructor(client) {
    this.client = client;
  }

  async funnel({ from, to, groupBy, names } = {}) {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    if (groupBy) qs.set('groupBy', groupBy);
    if (names && names.length) qs.set('names', names.join(','));
    const qsStr = qs.toString();
    return this.client.get(`/api/admin/behavior/funnel${qsStr ? `?${qsStr}` : ''}`, { cacheTTL: 0 });
  }

  async events({ name, userId, from, to, limit, offset } = {}) {
    const qs = new URLSearchParams();
    if (name) qs.set('name', name);
    if (userId) qs.set('user_id', userId);
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    if (limit) qs.set('limit', String(limit));
    if (offset) qs.set('offset', String(offset));
    const qsStr = qs.toString();
    return this.client.get(`/api/admin/behavior/events${qsStr ? `?${qsStr}` : ''}`, { cacheTTL: 0 });
  }
}

// TASK-168 / issue #274 — admin session-replay screen. Reads
// dathent-api-gateway-worker's GET /api/admin/replay/{sessions,session/:id,
// patterns} (own-D1 read over ux_sessions/ux_event_batches — see that
// worker's src/routes/admin-replay.ts), same non-cached convention as
// AdminBehaviorClient above (this is an ops tool, always wants live state).
class AdminReplayClient {
  constructor(client) {
    this.client = client;
  }

  async sessions({ from, to, userId, limit, offset } = {}) {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    if (userId) qs.set('user_id', userId);
    if (limit) qs.set('limit', String(limit));
    if (offset) qs.set('offset', String(offset));
    const qsStr = qs.toString();
    return this.client.get(`/api/admin/replay/sessions${qsStr ? `?${qsStr}` : ''}`, { cacheTTL: 0 });
  }

  async session(id) {
    return this.client.get(`/api/admin/replay/session/${encodeURIComponent(id)}`, { cacheTTL: 0 });
  }

  async patterns({ from, to } = {}) {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    const qsStr = qs.toString();
    return this.client.get(`/api/admin/replay/patterns${qsStr ? `?${qsStr}` : ''}`, { cacheTTL: 0 });
  }
}

// TASK-234 / issue #352 (EPIC-021 #227) — organization credits admin.
// Talks to dathent-api-gateway-worker's /api/admin/credits/* (an exact
// allowlist proxied to stripe-worker's /internal/credits/admin/*, see the
// gateway's src/routes/admin-credits.ts and stripe-worker's
// handlers/credits-admin.ts). Amounts are integer CENTS (1 credit = 1 EUR =
// 100 cents) — src/lib/adminCredits.js converts what the admin types. Every
// write needs a `reason`; the server audits it under the admin's user id.
// cacheTTL:0 on every read — an ops tool, always the live ledger.
class AdminCreditsClient {
  constructor(client) {
    this.client = client;
  }

  _qs(params = {}) {
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value != null && value !== '') qs.set(key, String(value));
    }
    const str = qs.toString();
    return str ? `?${str}` : '';
  }

  async balance(organizationId) {
    return this.client.get(`/api/admin/credits/balance${this._qs({ organizationId })}`, { cacheTTL: 0 });
  }

  // { organizationId, account?, kind?, from?, to?, limit?, offset? } —
  // build it with adminCredits.buildHistoryParams().
  async history(params) {
    return this.client.get(`/api/admin/credits/history${this._qs(params)}`, { cacheTTL: 0 });
  }

  async grants(organizationId) {
    return this.client.get(`/api/admin/credits/grants${this._qs({ organizationId })}`, { cacheTTL: 0 });
  }

  // { organizationId, account: 'main'|'bonus', amountCents, reason, idempotencyKey? }
  async grant(payload) {
    return this.client.post('/api/admin/credits/grant', payload);
  }

  // { organizationId, account, amountCents, reason, grantKey?, idempotencyKey? }
  async revoke(payload) {
    return this.client.post('/api/admin/credits/revoke', payload);
  }

  async freeze(organizationId, reason) {
    return this.client.post('/api/admin/credits/freeze', { organizationId, reason });
  }

  async unfreeze(organizationId, reason) {
    return this.client.post('/api/admin/credits/unfreeze', { organizationId, reason });
  }

  async fraudFlags({ organizationId, unresolved, limit } = {}) {
    return this.client.get(
      `/api/admin/credits/fraud-flags${this._qs({ organizationId, unresolved: unresolved ? '1' : undefined, limit })}`,
      { cacheTTL: 0 }
    );
  }

  async resolveFraudFlag(flagId, reason) {
    return this.client.post('/api/admin/credits/fraud-flags/resolve', { flagId, reason });
  }
}

// Export singleton instance
export const api = new DathentClient();

export default DathentClient;
